import { parseProviderSummary } from "../../experiments/providerExperimentTypes";
import type { GetListParams, Identifier, RaRecord } from "ra-core";

import type {
  Contact,
  ContactConversationMessage,
  ContactConversationPage,
  ContactConversationPageParams,
  DashboardCapabilities,
  ContactFilterFacets,
  EchoAcquisitionAttribution,
  InboxConversation,
  InboxConversationPage,
  InboxSummary,
  InboxTransitionInput,
  InboxTransitionResult,
  ListInboxConversationsParams,
  EchoJourneyEdge,
  EchoJourneyField,
  EchoJourneyNode,
  FinalVideoDelivery,
  I2VPromptAudit,
  I2VPromptAttempt,
  I2VPromptAuditSummary,
  I2VPromptAuthoring,
  I2VPromptDirectorSummary,
  EchoflicksSessionEvent,
  EchoflicksSessionCostLineItem,
  EchoflicksSessionFeedback,
  EchoflicksSessionFramingEvent,
  EchoflicksSessionJourney,
  EchoflicksSessionTimings,
  EchoflicksSessionMedia,
  EchoflicksSessionMessage,
  EchoflicksSessionRemake,
  EchoflicksSessionRenderCost,
  EchoflicksSessionRenderCostSummary,
  EchoflicksVideoSession,
  PosterEmailCapture,
  SendWhatsAppMessageInput,
  SendWhatsAppMessageResult,
  TestEchoLaunch,
  TestEchoLaunchRequest,
  TestEchoLaunchStatus,
  TestEchoPack,
  WhatsAppBroadcast,
  WhatsAppBroadcastAnalytics,
  WhatsAppBroadcastInput,
  WhatsAppBroadcastPreview,
  WhatsAppBroadcastRecipient,
  WhatsAppBroadcastTimelineBucket,
  WhatsAppMessagingCapability,
  WhatsAppOutboundMessage,
  WhatsAppSenderCatalog,
  WhatsAppTemplate,
  WhatsAppTemplateInput,
  WhatsAppTemplateLibraryFilters,
  WhatsAppTemplateLibraryImport,
  WhatsAppTemplateLibraryImportInput,
  WhatsAppTemplateLibraryImportResult,
  WhatsAppTemplateLibraryImportSyncResult,
  WhatsAppTemplateLibraryItem,
  WhatsAppTemplateLibraryPage,
  WhatsAppTemplateMediaAsset,
  WhatsAppTemplateMediaUploadInput,
} from "../../types";
import type { CrmDataProvider } from "../types";
import type {
  DurableEchoLedgerProjection,
  DurableRecoveryAction,
  DurableRecoveryActionRequest,
} from "../../echoflicks/durableEchoLedger";
import {
  mapSceneFlag,
  type EchoflicksSessionSceneFlag,
} from "../../echoflicks/sceneFlags";
import {
  parseWebTranscript,
  type EchoWebTranscript,
} from "../../echoflicks/webTranscript";
import {
  awsDashboardRequest,
  awsDashboardTextRequest,
  DashboardRequestError,
  getDefaultAwsMediaBucket,
} from "./client";
import {
  EMPTY_LIST_RESULT,
  resolveSavedEchoesFilter,
} from "../commons/savedEchoes";
import { collapseConversation, collapseEchoSessions } from "./dedupe";
import {
  ORGANIC_ECHO_ORIGIN,
  echoPagePath,
  echoesPath,
  splitEchoFilters,
  wantsServerPage,
} from "./echoesPath";
import { testEchoRequest } from "./testEchoClient";
import { fetchAllDashboardPages } from "./paginatedDashboard";

type AwsCollectionResponse<T> = {
  data?: T[];
  total?: number;
  has_more?: boolean;
};

type AwsItemResponse<T> = {
  data?: T;
};

type AwsResource =
  | "contacts"
  | "echoflicks_echoes"
  | "echoflicks_poster_emails"
  | "echoflicks_video_sessions"
  | "echoflicks_session_media"
  | "echoflicks_session_messages"
  | "echoflicks_session_feedback"
  | "echoflicks_session_events"
  | "echoflicks_session_costs"
  | "echoflicks_session_render_costs"
  | "echoflicks_session_cost_reconciliation"
  | "echoflicks_session_framing"
  | "echoflicks_session_scene_flags"
  | "whatsapp_templates"
  | "whatsapp_broadcasts"
  | "echoflicks_session_journeys"
  | "echoflicks_session_timings"
  | "echoflicks_remakes";

const awsResources = new Set<string>([
  "contacts",
  "echoflicks_echoes",
  "echoflicks_poster_emails",
  "echoflicks_video_sessions",
  "echoflicks_session_media",
  "echoflicks_session_messages",
  "echoflicks_session_feedback",
  "echoflicks_session_events",
  "echoflicks_session_costs",
  "echoflicks_session_render_costs",
  "echoflicks_session_cost_reconciliation",
  "echoflicks_session_framing",
  "echoflicks_session_scene_flags",
  "whatsapp_templates",
  "whatsapp_broadcasts",
  "echoflicks_session_journeys",
  "echoflicks_session_timings",
  "echoflicks_remakes",
]);

const relationEndpoints: Record<string, string> = {
  echoflicks_session_media: "media",
  echoflicks_session_messages: "messages",
  echoflicks_session_feedback: "feedback",
  echoflicks_session_events: "events",
  echoflicks_remakes: "remakes",
  echoflicks_session_costs: "costs",
  // ECL contract C5: one RENDER# rollup row per derivative render.
  echoflicks_session_render_costs: "render-costs",
  echoflicks_session_framing: "framing",
  // C2: staff scene flags + the residual-motion QC verdict (administrator-only).
  echoflicks_session_scene_flags: "scene-flags",
};

export function createAwsDashboardDataProvider(
  fallbackDataProvider: CrmDataProvider,
): CrmDataProvider {
  const provider = {
    ...fallbackDataProvider,
    getList: async (resource: string, params: any) => {
      if (!isAwsResource(resource)) {
        return fallbackDataProvider.getList(resource, params);
      }

      if (resource === "contacts") {
        return fetchAwsContactList(params);
      }

      if (isEchoListResource(resource)) {
        // "Saved echoes" toggle: the dashboard API knows nothing about the
        // CRM user's saves, so turn the toggle into an id filter first.
        const resolved = await resolveSavedEchoesFilter(params);
        if (resolved.empty) return EMPTY_LIST_RESULT;
        params = resolved.params;
        if (wantsServerPage(params)) {
          return fetchAwsEchoPage(resource, params);
        }
      }

      if (resource === "echoflicks_session_render_costs") {
        // Every render, newest first as the API orders them, plus the C5
        // summary block (first render, edits and remakes, all renders).
        return fetchAwsRenderCosts(params);
      }

      if (resource === "echoflicks_session_costs") {
        // The API already reads every COST# page. The Echo cost card must use
        // the entire ledger for its total, unknown count, chart and table.
        // EMC (contract C6): the response also says whether allocated
        // marketing was visible to the caller, and carries the summary.
        return fetchAwsSessionCosts(params);
      }

      const data = await fetchAwsList(resource, params);
      return applyListParams(data, params);
    },
    getOne: async (resource: string, params: any) => {
      if (!isAwsResource(resource)) {
        return fallbackDataProvider.getOne(resource, params);
      }

      const data = await fetchAwsOne(resource, params);
      return { data };
    },
    getMany: async (resource: string, params: any) => {
      if (!isAwsResource(resource)) {
        return fallbackDataProvider.getMany(resource, params);
      }

      const results = await Promise.all(
        params.ids.map((id: Identifier) => fetchAwsOne(resource, { id })),
      );
      return { data: results };
    },
    getManyReference: async (resource: string, params: any) => {
      if (!isAwsResource(resource)) {
        return fallbackDataProvider.getManyReference(resource, params);
      }

      const filter = {
        ...params.filter,
        [params.target]: params.id,
      };
      if (resource === "contacts") {
        return fetchAwsContactList({ ...params, filter });
      }
      const data = await fetchAwsList(resource, { ...params, filter });
      return applyListParams(data, { ...params, filter });
    },
    create: async (resource: string, params: any) => {
      assertWritableResource(resource);
      return fallbackDataProvider.create(resource, params);
    },
    update: async (resource: string, params: any) => {
      assertWritableResource(resource);
      return fallbackDataProvider.update(resource, params);
    },
    updateMany: async (resource: string, params: any) => {
      assertWritableResource(resource);
      return fallbackDataProvider.updateMany(resource, params);
    },
    delete: async (resource: string, params: any) => {
      assertWritableResource(resource);
      return fallbackDataProvider.delete(resource, params);
    },
    deleteMany: async (resource: string, params: any) => {
      assertWritableResource(resource);
      return fallbackDataProvider.deleteMany(resource, params);
    },
    getWhatsAppMessagingCapability: async (
      contactId: Identifier,
      senderKey?: string,
    ) => {
      const response = await awsDashboardRequest<{
        data: WhatsAppMessagingCapability;
      }>(
        `/whatsapp/contacts/${encodeURIComponent(String(contactId))}/messaging-capability${
          senderKey ? `?sender_key=${encodeURIComponent(senderKey)}` : ""
        }`,
      );
      return response.data;
    },
    getWhatsAppSenders: async () => {
      const response = await awsDashboardRequest<{
        data: WhatsAppSenderCatalog;
      }>("/whatsapp/senders");
      return response.data;
    },
    sendWhatsAppMessage: async (payload: SendWhatsAppMessageInput) => {
      const response = await awsDashboardRequest<{
        data: SendWhatsAppMessageResult;
      }>("/whatsapp/messages", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return response.data;
    },
    getContactOutboundMessages: async (contactId: Identifier) => {
      const response = await awsDashboardRequest<{
        data: WhatsAppOutboundMessage[];
      }>(
        `/contacts/${encodeURIComponent(String(contactId))}/outbound-messages?limit=25`,
      );
      return response.data ?? [];
    },
    getContactConversation: async (contactId: Identifier) => {
      const response = await awsDashboardRequest<{
        data: ContactConversationMessage[];
      }>(
        `/contacts/${encodeURIComponent(String(contactId))}/conversation?limit=100`,
      );
      return collapseConversation(
        (response.data ?? []).map(mapContactConversationMessage),
      );
    },
    getContactConversationPage: async (
      contactId: Identifier,
      {
        cursor,
        limit = 100,
        order = "DESC",
      }: ContactConversationPageParams = {},
    ): Promise<ContactConversationPage> => {
      const query = new URLSearchParams();
      query.set("limit", String(limit));
      query.set("order", order);
      if (cursor) query.set("cursor", cursor);
      const response = await awsDashboardRequest<{
        data?: Record<string, unknown>[];
        has_more?: boolean;
        next_cursor?: string | null;
      }>(
        `/contacts/${encodeURIComponent(String(contactId))}/conversation?${query.toString()}`,
      );
      return {
        data: collapseConversation(
          (response.data ?? []).map(mapContactConversationMessage),
        ),
        has_more: Boolean(response.has_more),
        next_cursor: response.next_cursor ?? null,
      };
    },
    listInboxConversations: async ({
      state,
      cursor,
      limit = 50,
    }: ListInboxConversationsParams): Promise<InboxConversationPage> => {
      const query = new URLSearchParams();
      query.set("state", state);
      query.set("limit", String(limit));
      if (cursor) query.set("cursor", cursor);
      const response = await awsDashboardRequest<{
        data?: Record<string, unknown>[];
        total?: number;
        has_more?: boolean;
        next_cursor?: string | null;
      }>(`/whatsapp/inbox?${query.toString()}`);
      const data = (response.data ?? []).map(mapInboxConversation);
      return {
        data,
        total: response.total ?? data.length,
        state,
        has_more: Boolean(response.has_more),
        next_cursor: response.next_cursor ?? null,
      };
    },
    getInboxSummary: async (): Promise<InboxSummary> => {
      const response = await awsDashboardRequest<{
        data?: Record<string, unknown>;
      }>("/whatsapp/inbox/summary");
      return mapInboxSummary(response.data ?? {});
    },
    getInboxConversation: async (
      contactId: Identifier,
    ): Promise<InboxConversation> => {
      const response = await awsDashboardRequest<{
        data?: Record<string, unknown>;
      }>(`/whatsapp/inbox/${encodeURIComponent(String(contactId))}`);
      return mapInboxConversation(
        response.data ?? emptyInboxRecord(contactId),
        contactId,
      );
    },
    claimInboxConversation: (
      contactId: Identifier,
      input?: InboxTransitionInput,
    ) => transitionInboxConversation(contactId, "claim", input),
    resolveInboxConversation: (
      contactId: Identifier,
      input?: InboxTransitionInput,
    ) => transitionInboxConversation(contactId, "resolve", input),
    reopenInboxConversation: (
      contactId: Identifier,
      input?: InboxTransitionInput,
    ) => transitionInboxConversation(contactId, "reopen", input),
    releaseInboxConversation: (
      contactId: Identifier,
      input?: InboxTransitionInput,
    ) => transitionInboxConversation(contactId, "release", input),
    getDashboardCapabilities: async (): Promise<DashboardCapabilities> => {
      return awsDashboardRequest<DashboardCapabilities>("/health");
    },
    getContactFilterFacets: async (): Promise<ContactFilterFacets> => {
      const response =
        await awsDashboardRequest<Partial<ContactFilterFacets>>(
          "/contacts/facets",
        );
      return {
        facets: response.facets ?? {},
        total: response.total ?? 0,
        truncated: response.truncated,
        scanned: response.scanned,
      };
    },
    listWhatsAppTemplates: async () => {
      const response = await awsDashboardRequest<{ data: WhatsAppTemplate[] }>(
        "/whatsapp/templates?limit=1000",
      );
      return (response.data ?? []).map(mapTemplate);
    },
    listWhatsAppTemplateLibrary: async (
      filters: WhatsAppTemplateLibraryFilters = {},
    ) => {
      const params = new URLSearchParams();
      params.set("limit", String(filters.limit ?? 25));
      if (filters.search) params.set("search", filters.search);
      if (filters.topic) params.set("topic", filters.topic);
      if (filters.usecase) params.set("usecase", filters.usecase);
      if (filters.industry) params.set("industry", filters.industry);
      if (filters.language) params.set("language", filters.language);
      if (filters.cursor) params.set("cursor", filters.cursor);
      const response = await awsDashboardRequest<
        Omit<WhatsAppTemplateLibraryPage, "data"> & {
          data?: Record<string, unknown>[];
        }
      >(`/whatsapp/template-library?${params.toString()}`);
      return {
        ...response,
        data: (response.data ?? []).map(mapTemplateLibraryItem),
        next_cursor: response.next_cursor ?? null,
      } satisfies WhatsAppTemplateLibraryPage;
    },
    importWhatsAppTemplateFromLibrary: async (
      payload: WhatsAppTemplateLibraryImportInput,
    ): Promise<WhatsAppTemplateLibraryImportResult> => {
      const response = await awsDashboardRequest<{
        data?: Record<string, unknown>;
        import?: Record<string, unknown>;
      }>("/whatsapp/template-library/import", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return {
        data: mapTemplate(response.data ?? {}),
        import: response.import
          ? mapTemplateLibraryImport(response.import)
          : undefined,
      };
    },
    listWhatsAppTemplateLibraryImports: async () => {
      const response = await awsDashboardRequest<{
        data?: Record<string, unknown>[];
      }>("/whatsapp/template-library/imports?limit=1000");
      return (response.data ?? []).map(mapTemplateLibraryImport);
    },
    syncWhatsAppTemplateLibraryImport: async (
      id: Identifier,
    ): Promise<WhatsAppTemplateLibraryImportSyncResult> => {
      const response = await awsDashboardRequest<{
        data?: Record<string, unknown>;
        template?: Record<string, unknown> | null;
      }>(
        `/whatsapp/template-library/imports/${encodeURIComponent(String(id))}/sync`,
        {
          method: "POST",
        },
      );
      return {
        data: mapTemplateLibraryImport(response.data ?? {}),
        template: response.template ? mapTemplate(response.template) : null,
      };
    },
    syncAllWhatsAppTemplates: async () => {
      const response = await awsDashboardRequest<{ data: WhatsAppTemplate[] }>(
        "/whatsapp/templates/sync-all",
        { method: "POST" },
      );
      return (response.data ?? []).map(mapTemplate);
    },
    createWhatsAppTemplate: async (payload: WhatsAppTemplateInput) => {
      const response = await awsDashboardRequest<{ data: WhatsAppTemplate }>(
        "/whatsapp/templates",
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );
      return mapTemplate(response.data ?? {});
    },
    uploadWhatsAppTemplateMedia: async (
      payload: WhatsAppTemplateMediaUploadInput,
    ) => {
      const response = await awsDashboardRequest<{
        data: WhatsAppTemplateMediaAsset;
      }>("/whatsapp/template-media", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return mapTemplateMedia(response.data ?? {});
    },
    updateWhatsAppTemplate: async (
      id: Identifier,
      payload: Partial<WhatsAppTemplateInput>,
    ) => {
      const response = await awsDashboardRequest<{ data: WhatsAppTemplate }>(
        `/whatsapp/templates/${encodeURIComponent(String(id))}`,
        {
          method: "PATCH",
          body: JSON.stringify(payload),
        },
      );
      return mapTemplate(response.data ?? {});
    },
    syncWhatsAppTemplate: async (id: Identifier) => {
      const response = await awsDashboardRequest<{ data: WhatsAppTemplate }>(
        `/whatsapp/templates/${encodeURIComponent(String(id))}/sync`,
        { method: "POST" },
      );
      return mapTemplate(response.data ?? {});
    },
    unpauseWhatsAppTemplate: async (id: Identifier) => {
      const response = await awsDashboardRequest<{ data: WhatsAppTemplate }>(
        `/whatsapp/templates/${encodeURIComponent(String(id))}/unpause`,
        { method: "POST" },
      );
      return mapTemplate(response.data ?? {});
    },
    addWhatsAppTemplateAppealNote: async (
      id: Identifier,
      note: string,
      operator?: { id?: Identifier; fullName?: string },
    ) => {
      const response = await awsDashboardRequest<{ data: WhatsAppTemplate }>(
        `/whatsapp/templates/${encodeURIComponent(String(id))}/appeal-note`,
        {
          method: "POST",
          body: JSON.stringify({
            note,
            operator_user_id: operator?.id,
            operator_name: operator?.fullName,
          }),
        },
      );
      return mapTemplate(response.data ?? {});
    },
    previewWhatsAppBroadcast: async (payload: WhatsAppBroadcastInput) => {
      const response = await awsDashboardRequest<{
        data: WhatsAppBroadcastPreview;
      }>("/whatsapp/broadcasts/preview", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      return {
        ...response.data,
        template: mapTemplate(response.data?.template ?? {}),
      } as WhatsAppBroadcastPreview;
    },
    createWhatsAppBroadcast: async (payload: WhatsAppBroadcastInput) => {
      const response = await awsDashboardRequest<{ data: WhatsAppBroadcast }>(
        "/whatsapp/broadcasts",
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );
      return mapBroadcast(response.data ?? {});
    },
    updateWhatsAppBroadcast: async (
      id: Identifier,
      payload: WhatsAppBroadcastInput,
    ) => {
      const response = await awsDashboardRequest<{ data: WhatsAppBroadcast }>(
        `/whatsapp/broadcasts/${encodeURIComponent(String(id))}`,
        {
          method: "PATCH",
          body: JSON.stringify(payload),
        },
      );
      return mapBroadcast(response.data ?? {});
    },
    listWhatsAppBroadcasts: async () => {
      const response = await awsDashboardRequest<{ data: WhatsAppBroadcast[] }>(
        "/whatsapp/broadcasts?limit=1000",
      );
      return (response.data ?? []).map(mapBroadcast);
    },
    getWhatsAppBroadcastRecipients: async (id: Identifier) => {
      const response = await awsDashboardRequest<{
        data: WhatsAppBroadcastRecipient[];
      }>(
        `/whatsapp/broadcasts/${encodeURIComponent(String(id))}/recipients?limit=1000`,
      );
      return (response.data ?? []).map(mapBroadcastRecipient);
    },
    getWhatsAppBroadcastAnalytics: async (id: Identifier) => {
      const response = await awsDashboardRequest<{
        data: WhatsAppBroadcastAnalytics;
        warnings?: WhatsAppBroadcastAnalytics["warnings"];
      }>(`/whatsapp/broadcasts/${encodeURIComponent(String(id))}/analytics`);
      return mapBroadcastAnalytics(response.data ?? {}, response.warnings);
    },
    getWhatsAppBroadcastTimeline: async (id: Identifier) => {
      const response = await awsDashboardRequest<{
        data: WhatsAppBroadcastTimelineBucket[];
      }>(`/whatsapp/broadcasts/${encodeURIComponent(String(id))}/timeline`);
      return response.data ?? [];
    },
    exportWhatsAppBroadcastRecipients: async (id: Identifier) => {
      return awsDashboardTextRequest(
        `/whatsapp/broadcasts/${encodeURIComponent(String(id))}/export.csv`,
        { headers: { Accept: "text/csv" } },
      );
    },
    transitionWhatsAppBroadcast: async (
      id: Identifier,
      action: "pause" | "resume" | "cancel" | "retry-failed",
    ) => {
      const response = await awsDashboardRequest<{ data: WhatsAppBroadcast }>(
        `/whatsapp/broadcasts/${encodeURIComponent(String(id))}/${action}`,
        { method: "POST" },
      );
      return mapBroadcast(response.data ?? {});
    },
    getDurableEchoLedger: async (
      workloadId: Identifier,
    ): Promise<DurableEchoLedgerProjection | null> => {
      const response = await awsDashboardRequest<{
        data?: DurableEchoLedgerProjection | null;
      }>(`/echoes/${encodeURIComponent(String(workloadId))}/durability`);
      return response.data ?? null;
    },
    getDurableEchoRecoveryActions: async (
      workloadId: Identifier,
    ): Promise<DurableRecoveryAction[]> => {
      const response = await awsDashboardRequest<{
        data?: DurableRecoveryAction[];
      }>(
        `/echoes/${encodeURIComponent(String(workloadId))}/durability/recovery-actions`,
      );
      return response.data ?? [];
    },
    createDurableEchoRecoveryAction: async (
      workloadId: Identifier,
      request: DurableRecoveryActionRequest,
    ): Promise<Record<string, unknown>> => {
      return awsDashboardRequest<Record<string, unknown>>(
        `/echoes/${encodeURIComponent(String(workloadId))}/durability/recovery-actions`,
        { method: "POST", body: JSON.stringify(request) },
      );
    },
    // Web Create (MYC-090): the my.echoflicks.com timeline of a web Echo
    // (contract C13). The route answers 404 for every WhatsApp Echo — that is
    // "not a web Echo", not an error, so it resolves to null (no error toast).
    getEchoWebTranscript: async (
      workloadId: Identifier,
    ): Promise<EchoWebTranscript | null> => {
      try {
        const response = await awsDashboardRequest<unknown>(
          `/echoes/${encodeURIComponent(String(workloadId))}/web-transcript`,
        );
        return parseWebTranscript(response);
      } catch (error) {
        if (error instanceof DashboardRequestError && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    // Echo re-make (Level-1 composer replay). Generate-first: this only mints a
    // new render + queued attempt — nothing reaches WhatsApp until approve. The
    // dashboard-api gates these to admins via the x-operator-user-id header.
    triggerSessionRemake: async (
      sessionId: Identifier,
      operator?: { id?: Identifier },
    ) => {
      const response = await awsDashboardRequest<{
        data: EchoflicksSessionRemake;
      }>(`/video-sessions/${encodeURIComponent(String(sessionId))}/remakes`, {
        method: "POST",
        headers: operatorHeader(operator),
      });
      return mapRemake(response.data ?? {}, sessionId);
    },
    approveSessionRemake: async (
      sessionId: Identifier,
      remakeId: Identifier,
      operator?: { id?: Identifier },
    ) => {
      const response = await awsDashboardRequest<{
        data: EchoflicksSessionRemake;
        skipped?: boolean;
        reason?: string;
      }>(
        `/video-sessions/${encodeURIComponent(String(sessionId))}/remakes/${encodeURIComponent(String(remakeId))}/approve`,
        {
          method: "POST",
          headers: operatorHeader(operator),
        },
      );
      return mapRemake(response.data ?? {}, sessionId);
    },
    rejectSessionRemake: async (
      sessionId: Identifier,
      remakeId: Identifier,
      operator?: { id?: Identifier },
    ) => {
      const response = await awsDashboardRequest<{
        data: EchoflicksSessionRemake;
      }>(
        `/video-sessions/${encodeURIComponent(String(sessionId))}/remakes/${encodeURIComponent(String(remakeId))}/reject`,
        {
          method: "POST",
          headers: operatorHeader(operator),
        },
      );
      return mapRemake(response.data ?? {}, sessionId);
    },
    // Test-echo autoqueue (plan 2026-09-02 §3.5-C). These three go to the BOT's
    // internal API through its own Vercel proxy — not the dashboard API — so they
    // use testEchoRequest, and the operator identity is derived server-side from
    // the Supabase session rather than sent from here.
    listTestEchoPacks: async (userId: string): Promise<TestEchoPack[]> => {
      const response = await testEchoRequest<{ data?: TestEchoPack[] }>(
        `/packs?user_id=${encodeURIComponent(userId)}`,
      );
      return response?.data ?? [];
    },
    launchTestEcho: async (
      request: TestEchoLaunchRequest,
    ): Promise<TestEchoLaunch> => {
      const response = await testEchoRequest<{ data?: TestEchoLaunch }>(
        "/launches",
        { method: "POST", body: JSON.stringify(request) },
      );
      if (!response?.data?.workload_id) {
        throw new Error("The bot accepted the launch but returned no workload");
      }
      return response.data;
    },
    getTestEchoLaunch: async (
      workloadId: string,
      userId: string,
      senderKey?: string,
    ): Promise<TestEchoLaunchStatus> => {
      // The bot reads the launch out of that user's live conversation state,
      // so the status route is user-scoped and rejects a call without it. The
      // sender scopes it further wherever more than one carries inbound —
      // without it the read binds a different conversation and reports
      // "unknown" for a live Echo.
      const query = new URLSearchParams({ user_id: userId });
      if (senderKey) query.set("sender_key", senderKey);
      const response = await testEchoRequest<{ data?: TestEchoLaunchStatus }>(
        `/launches/${encodeURIComponent(workloadId)}?${query.toString()}`,
      );
      return response?.data ?? { workload_id: workloadId, status: "unknown" };
    },
  } as CrmDataProvider;

  return provider;
}

// The dashboard-api admin-gates the remake endpoints on an x-operator-user-id
// header the CRM already sends elsewhere (broadcasts pass it in the body). We
// send it as a header per the remake API contract; omit it when unknown.
function operatorHeader(operator?: {
  id?: Identifier;
}): Record<string, string> {
  if (operator?.id == null) return {};
  return { "x-operator-user-id": String(operator.id) };
}

function isAwsResource(resource: string): resource is AwsResource {
  return awsResources.has(resource);
}

function assertWritableResource(resource: string) {
  if (isAwsResource(resource)) {
    throw new Error("Live AWS dashboard resources are read-only.");
  }
}

async function fetchAwsList(
  resource: AwsResource,
  params: Partial<GetListParams>,
): Promise<RaRecord[]> {
  if (resource === "whatsapp_templates") {
    return fetchCollection<WhatsAppTemplate>(
      "/whatsapp/templates",
      mapTemplate,
    );
  }

  if (resource === "whatsapp_broadcasts") {
    return fetchCollection<WhatsAppBroadcast>(
      "/whatsapp/broadcasts",
      mapBroadcast,
    );
  }

  if (resource === "echoflicks_echoes") {
    return collapseEchoSessions(
      await fetchCollection<EchoflicksVideoSession>(
        echoesPath(params.filter),
        mapSession,
      ),
    );
  }

  if (resource === "echoflicks_video_sessions") {
    return collapseEchoSessions(
      await fetchCollection<EchoflicksVideoSession>(
        echoesPath(params.filter, "/video-sessions"),
        mapSession,
      ),
    );
  }

  if (resource === "echoflicks_poster_emails") {
    return fetchCollection<PosterEmailCapture>(
      "/poster-emails",
      mapPosterEmail,
    );
  }

  if (resource === "echoflicks_session_journeys") {
    const sessionId = params.filter?.session_id ?? params.filter?.id;
    if (sessionId == null) {
      return [];
    }
    return [await fetchAwsJourney(sessionId)];
  }

  if (resource === "echoflicks_session_timings") {
    const sessionId = params.filter?.session_id ?? params.filter?.id;
    if (sessionId == null) {
      return [];
    }
    return [await fetchAwsTimings(sessionId)];
  }

  const sessionId = params.filter?.session_id;
  if (sessionId == null) {
    return [];
  }

  const endpoint = relationEndpoints[resource];
  const path = `/video-sessions/${encodeURIComponent(String(sessionId))}/${endpoint}`;

  if (resource === "echoflicks_session_media") {
    return fetchCollection<EchoflicksSessionMedia>(path, mapMedia);
  }
  if (resource === "echoflicks_session_framing") {
    return fetchCollection<EchoflicksSessionFramingEvent>(
      path,
      mapFramingEvent,
    );
  }
  if (resource === "echoflicks_session_messages") {
    return fetchCollection<EchoflicksSessionMessage>(path, mapMessage);
  }
  if (resource === "echoflicks_session_feedback") {
    return fetchCollection<EchoflicksSessionFeedback>(path, mapFeedback);
  }
  if (resource === "echoflicks_remakes") {
    return fetchCollection<EchoflicksSessionRemake>(path, (record) =>
      mapRemake(record, sessionId),
    );
  }
  if (resource === "echoflicks_session_costs") {
    return fetchCollection<EchoflicksSessionCostLineItem>(
      path,
      mapCostLineItem,
    );
  }
  if (resource === "echoflicks_session_render_costs") {
    return (await fetchRenderCostsResponse(sessionId)).data;
  }
  if (resource === "echoflicks_session_scene_flags") {
    return fetchCollection<EchoflicksSessionSceneFlag>(path, mapSceneFlag);
  }
  return fetchCollection<EchoflicksSessionEvent>(path, mapEvent);
}

/**
 * `GET /video-sessions/{id}/costs` with its EMC envelope (contract C6):
 * `{data, total, acquisition_visible, summary}`. `acquisition_visible` is
 * false for operators who are not administrators: the API removed the
 * marketing rows and the `echo_cost_acquisition_*` fields. Both are read
 * from the first page; an API that predates EMC sends neither (null).
 */
async function fetchAwsSessionCosts(params: Partial<GetListParams>) {
  const sessionId = params.filter?.session_id;
  if (sessionId == null) {
    return {
      data: [],
      total: 0,
      meta: { acquisitionVisible: null, summary: null },
    };
  }
  let acquisitionVisible: boolean | null = null;
  let summary: Record<string, unknown> | null = null;
  let firstPage = true;
  const request = async <T>(path: string): Promise<T> => {
    const page = await awsDashboardRequest<T>(path);
    if (firstPage && isRecord(page)) {
      firstPage = false;
      if (typeof page.acquisition_visible === "boolean") {
        acquisitionVisible = page.acquisition_visible;
      }
      if (isRecord(page.summary)) summary = page.summary;
    }
    return page;
  };
  const rows = await fetchAllDashboardPages<Record<string, unknown>>(
    `/video-sessions/${encodeURIComponent(String(sessionId))}/costs`,
    { pageSize: 1000, maxPages: 10, request },
  );
  const data = rows.map(mapCostLineItem);
  return {
    data,
    total: data.length,
    meta: { acquisitionVisible, summary } satisfies SessionCostsMeta,
  };
}

/** `meta` of the `echoflicks_session_costs` list (EMC contract C6). */
export type SessionCostsMeta = {
  acquisitionVisible: boolean | null;
  summary: Record<string, unknown> | null;
};

/**
 * `GET /video-sessions/{id}/render-costs` (ECL contract C5): one RENDER# row
 * per derivative render and a `summary`. A single query on the API side, so
 * one request here; the rows keep the API's order (newest first).
 */
async function fetchRenderCostsResponse(sessionId: Identifier): Promise<{
  data: EchoflicksSessionRenderCost[];
  summary: EchoflicksSessionRenderCostSummary | null;
}> {
  const response = await awsDashboardRequest<{
    data?: Record<string, unknown>[];
    summary?: Record<string, unknown> | null;
  }>(`/video-sessions/${encodeURIComponent(String(sessionId))}/render-costs`);
  const rows = Array.isArray(response?.data) ? response.data : [];
  return {
    data: rows.map(mapRenderCost),
    summary: mapRenderCostSummary(response?.summary),
  };
}

async function fetchAwsRenderCosts(params: Partial<GetListParams>) {
  const sessionId = params.filter?.session_id;
  if (sessionId == null) {
    return { data: [], total: 0, meta: { summary: null } };
  }
  const { data, summary } = await fetchRenderCostsResponse(sessionId);
  return { data, total: data.length, meta: { summary } };
}

async function fetchAwsOne(resource: string, params: { id: Identifier }) {
  if (resource === "echoflicks_session_cost_reconciliation") {
    const response = await awsDashboardRequest<
      AwsItemResponse<Record<string, unknown>>
    >(
      `/video-sessions/${encodeURIComponent(String(params.id))}/cost-reconciliation`,
    );
    return { ...response.data, id: params.id };
  }
  if (resource === "echoflicks_session_journeys") {
    return fetchAwsJourney(params.id);
  }

  if (resource === "echoflicks_session_timings") {
    return fetchAwsTimings(params.id);
  }

  if (
    resource === "echoflicks_video_sessions" ||
    resource === "echoflicks_echoes"
  ) {
    const response = await awsDashboardRequest<
      AwsItemResponse<Record<string, unknown>>
    >(`/video-sessions/${encodeURIComponent(String(params.id))}`);
    return mapSession(response.data ?? {});
  }

  const { data } = await providerListById(resource, params.id);
  const record = data.find((item) => item.id === params.id);
  if (!record) {
    throw new Error(`No ${resource} record found for id ${params.id}`);
  }
  return record;
}

async function fetchAwsJourney(
  id: Identifier,
): Promise<EchoflicksSessionJourney> {
  const response = await awsDashboardRequest<
    AwsItemResponse<Record<string, unknown>>
  >(`/video-sessions/${encodeURIComponent(String(id))}/journey`);
  return mapJourney(response.data ?? { session_id: id });
}

async function fetchAwsTimings(
  id: Identifier,
): Promise<EchoflicksSessionTimings> {
  const response = await awsDashboardRequest<
    AwsItemResponse<Record<string, unknown>>
  >(`/video-sessions/${encodeURIComponent(String(id))}/timings`);
  const data = (response.data ?? {}) as Partial<EchoflicksSessionTimings>;
  return {
    ...data,
    id,
    session_id: data.session_id ?? id,
    legs: Array.isArray(data.legs) ? data.legs : [],
    spans: Array.isArray(data.spans) ? data.spans : [],
    gaps: Array.isArray(data.gaps) ? data.gaps : [],
    totals: data.totals ?? {},
  };
}

async function providerListById(resource: string, id: Identifier) {
  if (resource === "contacts") {
    const contact = await fetchAwsContactById(id);
    return { data: contact ? [contact] : [], total: contact ? 1 : 0 };
  }

  return applyListParams(await fetchAwsList(resource as AwsResource, {}), {
    filter: { id },
    pagination: { page: 1, perPage: 1 },
    sort: { field: "id", order: "ASC" },
  });
}

const ECHO_LIST_ENDPOINTS: Record<string, string> = {
  echoflicks_echoes: "/echoes",
  echoflicks_video_sessions: "/video-sessions",
};

function isEchoListResource(resource: string): boolean {
  return resource in ECHO_LIST_ENDPOINTS;
}

/**
 * One server-side page of echoes or sessions, for list views that show 25–100
 * rows. Before this the list downloaded the whole partition (every page of
 * 1000 rows, ~3 MB) and paginated in the browser.
 *
 * The API sorts on its index (`display_date` → latest_event_at) and applies the
 * filters it knows (see echoesPath.ts); anything else is matched on the page
 * that comes back. Its `total` is a running "there is more" sentinel rather
 * than a count, so the list paginates on `has_more` and only reports an exact
 * total once the last page has been reached.
 */
async function fetchAwsEchoPage(
  resource: string,
  params: Partial<GetListParams>,
) {
  const { server, client } = splitEchoFilters(params.filter);
  const page = Math.max(1, params.pagination?.page ?? 1);
  const perPage = Math.max(1, params.pagination?.perPage ?? 25);
  const response = await awsDashboardRequest<
    AwsCollectionResponse<Record<string, unknown>>
  >(echoPagePath(ECHO_LIST_ENDPOINTS[resource], params, server));
  const rows = collapseEchoSessions(
    (response.data ?? []).map(mapSession),
  ).filter((record) => matchesFilter(record, client));
  const hasNextPage = Boolean(response.has_more);
  return {
    data: rows,
    total: hasNextPage ? undefined : (page - 1) * perPage + rows.length,
    pageInfo: { hasNextPage, hasPreviousPage: page > 1 },
  };
}

async function fetchAwsContactList(params: Partial<GetListParams>) {
  const query = buildAwsCollectionQuery(params);
  const response = await awsDashboardRequest<
    AwsCollectionResponse<Record<string, unknown>>
  >(`/contacts?${query}`);
  const data = (response.data ?? []).map(mapContact);
  return {
    data,
    total: response.total ?? data.length,
    pageInfo: {
      hasNextPage: Boolean(response.has_more),
      hasPreviousPage: (params.pagination?.page ?? 1) > 1,
    },
  };
}

/**
 * One contact by id via the item endpoint. The previous lookup asked the list
 * endpoint for `id=…`, and that path scans the whole contact partition in
 * memory to compute an exact total — for a single record. Builds of the API
 * that predate the item route answer 404, so fall back to the list then.
 */
async function fetchAwsContactById(id: Identifier): Promise<Contact | null> {
  try {
    const response = await awsDashboardRequest<
      AwsItemResponse<Record<string, unknown>>
    >(`/contacts/${encodeURIComponent(String(id))}`);
    return response.data ? mapContact(response.data) : null;
  } catch (error) {
    if (error instanceof DashboardRequestError && error.status === 404) {
      const { data } = await fetchAwsContactList({
        filter: { id },
        pagination: { page: 1, perPage: 1 },
        sort: { field: "id", order: "ASC" },
      });
      return data[0] ?? null;
    }
    throw error;
  }
}

export function buildAwsCollectionQuery(params: Partial<GetListParams>) {
  const query = new URLSearchParams();
  const page = Math.max(1, params.pagination?.page ?? 1);
  const perPage = Math.max(1, params.pagination?.perPage ?? 1000);
  query.set("limit", String(perPage));
  query.set("offset", String((page - 1) * perPage));

  if (params.sort?.field) {
    query.set("sort", params.sort.field);
    query.set("order", params.sort.order ?? "ASC");
  }

  Object.entries(params.filter ?? {}).forEach(([key, value]) => {
    const formatted = formatAwsQueryValue(value);
    if (formatted == null) return;
    query.set(key, formatted);
  });

  return query.toString();
}

function formatAwsQueryValue(value: unknown) {
  if (value == null || value === "") return null;
  if (Array.isArray(value)) {
    const items = value.map((item) => String(item).trim()).filter(Boolean);
    return items.length ? items.join(",") : null;
  }
  return String(value);
}

async function fetchCollection<T extends RaRecord>(
  path: string,
  mapRecord: (record: Record<string, unknown>) => T,
): Promise<RaRecord[]> {
  const data = await fetchAllDashboardPages<Record<string, unknown>>(path, {
    pageSize: 1000,
    maxPages: 10,
  });
  return data.map(mapRecord);
}

function applyListParams<T extends RaRecord>(
  records: T[],
  params: Partial<GetListParams>,
) {
  const filtered = records.filter((record) =>
    matchesFilter(record, params.filter),
  );
  const sorted = sortRecords(filtered, params.sort);
  const page = params.pagination?.page ?? 1;
  const perPage = params.pagination?.perPage ?? (sorted.length || 1);
  const start = (page - 1) * perPage;

  return {
    data: sorted.slice(start, start + perPage),
    total: sorted.length,
  };
}

function filterComparison(recordValue: unknown, value: unknown): number {
  // Null/empty record values never satisfy a range comparison, matching
  // postgrest NULL semantics (NaN makes every >/>=/</<= return false).
  if (recordValue == null || recordValue === "") return Number.NaN;
  const recordNumber = Number(recordValue);
  const filterNumber = Number(value);
  if (Number.isFinite(recordNumber) && Number.isFinite(filterNumber)) {
    return recordNumber - filterNumber;
  }
  // ISO timestamps and other strings compare correctly lexicographically.
  return String(recordValue).localeCompare(String(value));
}

function filterIncludesText(recordValue: unknown, value: unknown): boolean {
  return String(recordValue ?? "")
    .toLowerCase()
    .includes(String(value).replace(/[%*]/g, "").toLowerCase());
}

function filterToList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String);
  // postgrest "in" syntax: "(a,b,c)"
  return String(value)
    .replace(/^\(|\)$/g, "")
    .split(",")
    .map((item) => item.trim().replace(/^"|"$/g, ""));
}

const FILTER_OPERATORS: Record<
  string,
  (recordValue: unknown, value: unknown) => boolean
> = {
  eq: (a, b) => String(a ?? "") === String(b),
  neq: (a, b) => String(a ?? "") !== String(b),
  gt: (a, b) => filterComparison(a, b) > 0,
  gte: (a, b) => filterComparison(a, b) >= 0,
  lt: (a, b) => filterComparison(a, b) < 0,
  lte: (a, b) => filterComparison(a, b) <= 0,
  like: filterIncludesText,
  ilike: filterIncludesText,
  in: (a, b) => filterToList(b).includes(String(a ?? "")),
};

/**
 * Mirror the dashboard-API's sentinel semantics client-side, so a filter the API
 * already honoured cannot blank the rows it returned. Today the only such field
 * is `echo_origin`, where "absent" is spelled `organic` on the wire (a test Echo
 * carries "test_autoqueue"; an organic Echo carries nothing at all).
 */
function normalizeFilterField(field: string, recordValue: unknown) {
  if (field === "echo_origin" && (recordValue == null || recordValue === "")) {
    return ORGANIC_ECHO_ORIGIN;
  }
  return recordValue;
}

export function matchesFilter(
  record: RaRecord,
  filter: GetListParams["filter"] = {},
) {
  return Object.entries(filter).every(([key, value]) => {
    if (value == null || value === "") return true;

    if (key === "q") {
      return JSON.stringify(record)
        .toLowerCase()
        .includes(String(value).toLowerCase());
    }

    // Filters use the ra-data-postgrest "field@operator" convention
    // (e.g. last_echo_at@gte). Parse the operator and evaluate it against the
    // record; a bare key means equality (array value behaves like "in").
    const atIndex = key.indexOf("@");
    const field = atIndex === -1 ? key : key.slice(0, atIndex);
    const operatorKey = atIndex === -1 ? "" : key.slice(atIndex + 1);
    const recordValue = normalizeFilterField(
      field,
      (record as Record<string, unknown>)[field],
    );

    if (!operatorKey) {
      if (Array.isArray(value)) {
        return value.map(String).includes(String(recordValue));
      }
      return String(recordValue ?? "") === String(value);
    }

    const operator = FILTER_OPERATORS[operatorKey];
    // Unknown operator: stay lenient so an unmodelled filter shape can never
    // blank the entire list (treat as a no-op match).
    if (!operator) return true;
    return operator(recordValue, value);
  });
}

function sortRecords<T extends RaRecord>(
  records: T[],
  sort: GetListParams["sort"],
) {
  if (!sort?.field) return records;

  return [...records].sort((left, right) => {
    const leftValue = (left as Record<string, unknown>)[sort.field];
    const rightValue = (right as Record<string, unknown>)[sort.field];

    if (leftValue == null && rightValue == null) return 0;
    if (leftValue == null) return 1;
    if (rightValue == null) return -1;

    const comparison = String(leftValue).localeCompare(String(rightValue));
    return sort.order === "DESC" ? -comparison : comparison;
  });
}

function mapPosterEmail(record: Record<string, unknown>): PosterEmailCapture {
  const email = stringValue(record.email);
  const contactId = stringValue(
    record.contact_id ?? record.whatsapp_user_id ?? "",
  );
  return {
    id: stringValue(record.id ?? `${contactId}:${email}`),
    email,
    contact_id: contactId,
    whatsapp_user_id: stringValue(record.whatsapp_user_id ?? contactId),
    sent_count: optionalNumberValue(record.sent_count) ?? 0,
    first_sent_at: stringValue(record.first_sent_at ?? record.last_sent_at),
    last_sent_at: stringValue(record.last_sent_at ?? ""),
    last_workload_id: nullableString(record.last_workload_id),
  };
}

// Exported for tests (EchoCostSection.test.ts).
export function mapSession(
  record: Record<string, unknown>,
): EchoflicksVideoSession {
  const mediaBucket = getDefaultAwsMediaBucket();
  const id = stringValue(record.session_id ?? record.id ?? record.source_id);
  // null, not "": consumers fall back to the session's media rows with `??`,
  // which only fires on null/undefined. An empty string is a value, so it wins
  // the fallback and then reads as "no media" downstream — a delivered echo
  // whose profile row never got its final_video_s3_key projected would render
  // as "No final video was found for this echo yet" while the media row (and
  // the file itself) were right there.
  const finalVideoKey = nullableString(
    record.final_video_storage_path ?? record.final_video_s3_key,
  );
  const scriptKey = nullableString(
    record.script_storage_path ?? record.script_s3_key,
  );
  const displayDate = stringValue(
    record.display_date ??
      record.completed_at ??
      record.latest_event_at ??
      record.started_at,
  );

  return {
    ...record,
    i2v_provider_summary: parseProviderSummary(record.i2v_provider_summary),
    id,
    source_id: stringValue(record.source_id ?? id),
    session_id: stringValue(record.session_id ?? id),
    workload_id: nullableString(record.workload_id),
    whatsapp_user_id: stringValue(
      record.whatsapp_user_id ?? record.contact_id ?? "unknown",
    ),
    contact_id: nullableString(record.contact_id ?? record.whatsapp_user_id),
    status: stringValue(
      record.status ?? (record.has_final_video ? "completed" : "active"),
    ),
    source: stringValue(record.source ?? "aws-live"),
    source_confidence: stringValue(record.source_confidence ?? "event"),
    echo_type: nullableString(record.echo_type),
    echo_origin: nullableString(record.echo_origin),
    test_pack_id: nullableString(record.test_pack_id),
    test_label: nullableString(record.test_label),
    // Studio film facts (B6.3): ids only; e-mails are resolved in the CRM.
    studio_echo_id: nullableString(record.studio_echo_id),
    studio_agency_id: nullableString(record.studio_agency_id),
    studio_created_by: nullableString(record.studio_created_by),
    studio_created_by_staff:
      typeof record.studio_created_by_staff === "boolean"
        ? record.studio_created_by_staff
        : null,
    studio_listing_group: nullableString(record.studio_listing_group),
    studio_revision_index:
      optionalNumberValue(record.studio_revision_index) ?? null,
    studio_parent_echo_id: nullableString(record.studio_parent_echo_id),
    studio_format: nullableString(record.studio_format),
    content_language: nullableString(record.content_language),
    content_language_source: nullableString(record.content_language_source),
    occasion_key: nullableString(record.occasion_key),
    occasion_label: nullableString(record.occasion_label),
    occasion_taxonomy_version: nullableString(record.occasion_taxonomy_version),
    occasion_selection_source: nullableString(record.occasion_selection_source),
    operator_synopsis: nullableString(record.operator_synopsis),
    operator_synopsis_schema_version: nullableString(
      record.operator_synopsis_schema_version,
    ),
    voice_id: nullableString(record.voice_id),
    campaign_source: nullableString(record.campaign_source),
    display_date: displayDate,
    movie_title: nullableString(record.movie_title ?? record.title),
    final_video_storage_bucket: nullableString(
      record.final_video_storage_bucket ??
        record.final_video_s3_bucket ??
        mediaBucket,
    ),
    final_video_storage_path: finalVideoKey,
    script_storage_bucket: nullableString(
      record.script_storage_bucket ?? mediaBucket,
    ),
    script_storage_path: scriptKey,
    rating: nullableNumber(record.rating),
    feedback_text: nullableString(record.feedback_text),
    feedback_at: nullableString(record.feedback_at),
    poster_storage_key: nullableString(record.poster_storage_key),
    // Posters live in the same media bucket as the final video; the projection
    // only carries a bucket when the event named one.
    poster_storage_bucket: nullableString(
      record.poster_storage_bucket ??
        record.final_video_storage_bucket ??
        record.final_video_s3_bucket ??
        mediaBucket,
    ),
    poster_width: optionalNumberValue(record.poster_width) ?? null,
    poster_height: optionalNumberValue(record.poster_height) ?? null,
    poster_byte_size: optionalNumberValue(record.poster_byte_size) ?? null,
    poster_mime_type: nullableString(record.poster_mime_type),
    poster_generated_at: nullableString(record.poster_generated_at),
    identity_summary: record.identity_summary,
    identity_availability: nullableString(record.identity_availability),
    identity_manifest_status: nullableString(record.identity_manifest_status),
    i2v_cost_total_usd: optionalNumberValue(record.i2v_cost_total_usd) ?? null,
    i2v_cost_estimated_total_usd:
      optionalNumberValue(record.i2v_cost_estimated_total_usd) ?? null,
    i2v_cost_provider_reported_total_usd:
      optionalNumberValue(record.i2v_cost_provider_reported_total_usd) ?? null,
    i2v_cost_unknown_count:
      optionalNumberValue(record.i2v_cost_unknown_count) ?? null,
    i2v_cost_line_item_count:
      optionalNumberValue(record.i2v_cost_line_item_count) ?? null,
    i2v_cost_billable_attempt_count:
      optionalNumberValue(record.i2v_cost_billable_attempt_count) ?? null,
    i2v_cost_rejected_attempt_count:
      optionalNumberValue(record.i2v_cost_rejected_attempt_count) ?? null,
    i2v_cost_accepted_segment_count:
      optionalNumberValue(record.i2v_cost_accepted_segment_count) ?? null,
    i2v_cost_by_provider: moneyMap(record.i2v_cost_by_provider),
    i2v_cost_by_concrete_provider: moneyMap(
      record.i2v_cost_by_concrete_provider,
    ),
    i2v_cost_by_provider_tier: moneyMap(record.i2v_cost_by_provider_tier),
    i2v_cost_by_model: moneyMap(record.i2v_cost_by_model),
    i2v_cost_by_segment: moneyMap(record.i2v_cost_by_segment),
    i2v_cost_by_status: moneyMap(record.i2v_cost_by_status),
    i2v_cost_currency: nullableString(record.i2v_cost_currency),
    i2v_cost_confidence: nullableString(record.i2v_cost_confidence),
    i2v_cost_price_version: nullableString(record.i2v_cost_price_version),
    i2v_cost_first_at: nullableString(record.i2v_cost_first_at),
    i2v_cost_last_at: nullableString(record.i2v_cost_last_at),
    i2v_cost_updated_at: nullableString(record.i2v_cost_updated_at),
    echo_cost_total_usd:
      optionalNumberValue(record.echo_cost_total_usd) ?? null,
    echo_cost_estimated_total_usd:
      optionalNumberValue(record.echo_cost_estimated_total_usd) ?? null,
    echo_cost_by_domain: moneyMap(record.echo_cost_by_domain),
    echo_cost_by_provider: moneyMap(record.echo_cost_by_provider),
    echo_cost_by_model: moneyMap(record.echo_cost_by_model),
    echo_cost_by_billing_channel: moneyMap(record.echo_cost_by_billing_channel),
    echo_cost_line_item_count:
      optionalNumberValue(record.echo_cost_line_item_count) ?? null,
    echo_cost_request_count:
      optionalNumberValue(record.echo_cost_request_count) ?? null,
    echo_cost_unknown_count:
      optionalNumberValue(record.echo_cost_unknown_count) ?? null,
    echo_cost_currency: nullableString(record.echo_cost_currency),
    echo_cost_confidence: nullableString(record.echo_cost_confidence),
    echo_cost_updated_at: nullableString(record.echo_cost_updated_at),
    echo_cost_input_tokens:
      optionalNumberValue(record.echo_cost_input_tokens) ?? null,
    echo_cost_cached_input_tokens:
      optionalNumberValue(record.echo_cost_cached_input_tokens) ?? null,
    echo_cost_uncached_input_tokens:
      optionalNumberValue(record.echo_cost_uncached_input_tokens) ?? null,
    echo_cost_output_tokens:
      optionalNumberValue(record.echo_cost_output_tokens) ?? null,
    echo_cost_reasoning_tokens:
      optionalNumberValue(record.echo_cost_reasoning_tokens) ?? null,
    echo_cost_visible_output_tokens:
      optionalNumberValue(record.echo_cost_visible_output_tokens) ?? null,
    echo_cost_image_text_input_tokens:
      optionalNumberValue(record.echo_cost_image_text_input_tokens) ?? null,
    echo_cost_image_input_tokens:
      optionalNumberValue(record.echo_cost_image_input_tokens) ?? null,
    echo_cost_image_output_tokens:
      optionalNumberValue(record.echo_cost_image_output_tokens) ?? null,
    // ECL contract C3. Absent on sessions indexed before ECL: the maps stay
    // undefined (not {}) so "no data" and "nothing in it" read differently.
    echo_cost_provider_credits_by_provider: optionalMoneyMap(
      record.echo_cost_provider_credits_by_provider,
    ),
    echo_cost_priced_by_rate_card_usd:
      optionalNumberValue(record.echo_cost_priced_by_rate_card_usd) ?? null,
    echo_cost_unknown_by_reason: optionalMoneyMap(
      record.echo_cost_unknown_by_reason,
    ),
    echo_cost_first_render_usd:
      optionalNumberValue(record.echo_cost_first_render_usd) ?? null,
    echo_cost_derivatives_usd:
      optionalNumberValue(record.echo_cost_derivatives_usd) ?? null,
    echo_cost_derivatives_count:
      optionalNumberValue(record.echo_cost_derivatives_count) ?? null,
    echo_cost_derivatives_by_kind: optionalMoneyMap(
      record.echo_cost_derivatives_by_kind,
    ),
    echo_cost_derivatives_unknown_count:
      optionalNumberValue(record.echo_cost_derivatives_unknown_count) ?? null,
    echo_cost_all_renders_usd:
      optionalNumberValue(record.echo_cost_all_renders_usd) ?? null,
    echo_cost_derivatives_updated_at: nullableString(
      record.echo_cost_derivatives_updated_at,
    ),
    // EMC contract C3.7. Absent on sessions indexed before EMC; Decimals may
    // arrive as strings. The acquisition fields reach admins only (C6).
    echo_cost_production_usd:
      optionalNumberValue(record.echo_cost_production_usd) ?? null,
    echo_cost_messaging_usd:
      optionalNumberValue(record.echo_cost_messaging_usd) ?? null,
    echo_cost_acquisition_usd:
      optionalNumberValue(record.echo_cost_acquisition_usd) ?? null,
    echo_cost_all_in_usd:
      optionalNumberValue(record.echo_cost_all_in_usd) ?? null,
    echo_cost_whatsapp_messages:
      optionalNumberValue(record.echo_cost_whatsapp_messages) ?? null,
    echo_cost_whatsapp_charged:
      optionalNumberValue(record.echo_cost_whatsapp_charged) ?? null,
    echo_cost_whatsapp_free:
      optionalNumberValue(record.echo_cost_whatsapp_free) ?? null,
    echo_cost_whatsapp_free_by_type: optionalMoneyMap(
      record.echo_cost_whatsapp_free_by_type,
    ),
    echo_cost_whatsapp_by_purpose: optionalMoneyMap(
      record.echo_cost_whatsapp_by_purpose,
    ),
    echo_cost_whatsapp_eur:
      optionalNumberValue(record.echo_cost_whatsapp_eur) ?? null,
    echo_cost_acquisition_by_pool: optionalMoneyMap(
      record.echo_cost_acquisition_by_pool,
    ),
    echo_cost_acquisition_eur:
      optionalNumberValue(record.echo_cost_acquisition_eur) ?? null,
    echo_cost_acquisition_status: nullableString(
      record.echo_cost_acquisition_status,
    ),
    echo_cost_acquisition_attribution: mapAcquisitionAttribution(
      record.echo_cost_acquisition_attribution,
    ),
    echo_cost_derivatives_messaging_usd:
      optionalNumberValue(record.echo_cost_derivatives_messaging_usd) ?? null,
    echo_cost_all_renders_messaging_usd:
      optionalNumberValue(record.echo_cost_all_renders_messaging_usd) ?? null,
    // HDW contract v1 §4. Absent on sessions indexed before HDW-023: the maps
    // stay undefined so the card shows no HD split for old Echoes.
    echo_cost_image_hd_by_outcome: optionalMoneyMap(
      record.echo_cost_image_hd_by_outcome,
    ),
    echo_cost_image_hd_outcome_counts: optionalMoneyMap(
      record.echo_cost_image_hd_outcome_counts,
    ),
    echo_cost_image_hd_avoided_usd:
      optionalNumberValue(record.echo_cost_image_hd_avoided_usd) ?? null,
    echo_cost_image_hd_avoided_count:
      optionalNumberValue(record.echo_cost_image_hd_avoided_count) ?? null,
    echo_cost_image_hd_outcome_basis: nullableString(
      record.echo_cost_image_hd_outcome_basis,
    ),
    is_derivative: optionalBooleanValue(record.is_derivative),
    derivative_kind: nullableString(record.derivative_kind),
    parent_session_id: nullableString(record.parent_session_id),
    parent_workload_id: nullableString(record.parent_workload_id),
    ledger_only: optionalBooleanValue(record.ledger_only),
    remake_id: nullableString(record.remake_id),
    recovery_mode: nullableString(record.recovery_mode),
    raw_metadata: (record.raw_metadata as Record<string, unknown>) ?? record,
  } satisfies EchoflicksVideoSession;
}

function mapRemake(
  record: Record<string, unknown>,
  sessionId?: Identifier | null,
): EchoflicksSessionRemake {
  const mediaBucket = getDefaultAwsMediaBucket();
  const id = stringValue(
    record.id ?? record.new_workload_id ?? record.workload_id,
  );
  const previewKey = nullableString(record.preview_key);
  const previewBucket = nullableString(record.preview_bucket ?? mediaBucket);
  return {
    ...record,
    id,
    session_id: nullableString(record.session_id ?? sessionId),
    new_workload_id: nullableString(
      record.new_workload_id ?? record.workload_id,
    ),
    status: stringValue(record.status ?? "queued"),
    requested_by: nullableString(record.requested_by),
    requested_at: nullableString(record.requested_at),
    rendered_at: nullableString(record.rendered_at),
    approved_at: nullableString(record.approved_at),
    delivered_at: nullableString(record.delivered_at),
    preview_bucket: previewBucket,
    preview_key: previewKey,
    movie_title: nullableString(record.movie_title),
    error: nullableString(record.error),
    recovery_mode: nullableString(record.recovery_mode),
    // ECL contract C5: the remake's render ledger, when the API sends it.
    cost_total_usd: optionalNumberValue(record.cost_total_usd) ?? null,
    cost_unknown_count: optionalNumberValue(record.cost_unknown_count) ?? null,
    cost_line_item_count:
      optionalNumberValue(record.cost_line_item_count) ?? null,
    cost_recorded: optionalBooleanValue(record.cost_recorded),
    // Map the preview pointer onto the final-video-style fields so the shared
    // useMediaUrl hook (signs via /media/sign) plays the preview like a final.
    final_video_storage_bucket: previewKey ? previewBucket : null,
    final_video_storage_path: previewKey,
  } satisfies EchoflicksSessionRemake;
}

// Exported for tests (costCollection.test.ts).
export function mapRenderCost(
  record: Record<string, unknown>,
): EchoflicksSessionRenderCost {
  const renderWorkloadId = stringValue(
    record.render_workload_id ?? record.id ?? record.render_id,
  );
  return {
    ...record,
    id: renderWorkloadId,
    render_workload_id: renderWorkloadId,
    render_kind: nullableString(record.render_kind),
    render_id: nullableString(record.render_id),
    parent_session_id: nullableString(record.parent_session_id),
    requested_at: nullableString(record.requested_at),
    status: nullableString(record.status),
    total_usd: optionalNumberValue(record.total_usd) ?? null,
    descendants_total_usd:
      optionalNumberValue(record.descendants_total_usd) ?? null,
    by_domain: moneyMap(record.by_domain),
    by_billing_channel: moneyMap(record.by_billing_channel),
    unknown_count: optionalNumberValue(record.unknown_count) ?? null,
    line_item_count: optionalNumberValue(record.line_item_count) ?? null,
    updated_at: nullableString(record.updated_at),
    ledger_only: optionalBooleanValue(record.ledger_only),
    cost_recorded: optionalBooleanValue(record.cost_recorded),
  } satisfies EchoflicksSessionRenderCost;
}

export function mapRenderCostSummary(
  value: unknown,
): EchoflicksSessionRenderCostSummary | null {
  if (!isRecord(value)) return null;
  return {
    first_render_usd: optionalNumberValue(value.first_render_usd) ?? null,
    derivatives_usd: optionalNumberValue(value.derivatives_usd) ?? null,
    all_renders_usd: optionalNumberValue(value.all_renders_usd) ?? null,
    derivatives_count: optionalNumberValue(value.derivatives_count) ?? null,
    by_kind: moneyMap(value.by_kind),
  };
}

function mapContact(record: Record<string, unknown>): Contact {
  const id = stringValue(
    record.contact_id ?? record.id ?? record.whatsapp_user_id,
  );
  const name = stringValue(record.contact_name ?? record.name ?? id);
  const [firstName, ...lastNameParts] = name.split(" ");
  const whatsappId = stringValue(record.whatsapp_user_id ?? id);

  return {
    ...record,
    id,
    first_name: stringValue(record.first_name ?? firstName ?? "WhatsApp"),
    last_name: stringValue(record.last_name ?? lastNameParts.join(" ")),
    title: stringValue(record.title ?? "Echoflicks contact"),
    email_jsonb: Array.isArray(record.email_jsonb) ? record.email_jsonb : [],
    phone_jsonb: [{ type: "Other", number: whatsappId }],
    first_seen: stringValue(record.first_seen ?? record.latest_event_at),
    last_seen: stringValue(record.last_seen ?? record.latest_event_at),
    has_newsletter: Boolean(record.has_newsletter),
    tags: Array.isArray(record.tags) ? record.tags : [],
    gender: stringValue(record.gender),
    status: stringValue(record.status ?? "active"),
    background: stringValue(
      record.background ?? `Live AWS contact projection for ${whatsappId}`,
    ),
    use_count: optionalNumberValue(record.use_count) ?? null,
    completed_echo_count:
      optionalNumberValue(record.completed_echo_count ?? record.video_count) ??
      null,
    video_count: optionalNumberValue(record.video_count) ?? null,
    average_rating: optionalNumberValue(record.average_rating) ?? null,
    rating_count: optionalNumberValue(record.rating_count) ?? null,
    last_rating: optionalNumberValue(record.last_rating) ?? null,
    last_rated_echo_title: nullableString(record.last_rated_echo_title),
    last_rated_session_id: nullableString(record.last_rated_session_id),
    last_echo_title: nullableString(record.last_echo_title),
    last_echo_session_id: nullableString(record.last_echo_session_id),
    use_count_bucket: nullableString(record.use_count_bucket),
    video_count_bucket: nullableString(record.video_count_bucket),
    average_rating_bucket: nullableString(record.average_rating_bucket),
    rating_bucket: nullableString(record.rating_bucket),
    lifecycle_stage: nullableString(record.lifecycle_stage),
    has_completed_video:
      typeof record.has_completed_video === "boolean"
        ? record.has_completed_video
        : null,
    first_echo_at: nullableString(record.first_echo_at),
    last_echo_at: nullableString(record.last_echo_at),
    first_completed_echo_at: nullableString(record.first_completed_echo_at),
    last_completed_echo_at: nullableString(record.last_completed_echo_at),
    first_session_at: nullableString(record.first_session_at),
    last_session_at: nullableString(record.last_session_at),
    failed_session_count:
      optionalNumberValue(record.failed_session_count) ?? null,
    whatsapp_opt_in_status: nullableString(record.whatsapp_opt_in_status),
    whatsapp_consent_state: nullableString(record.whatsapp_consent_state),
    whatsapp_stopped:
      typeof record.whatsapp_stopped === "boolean"
        ? record.whatsapp_stopped
        : null,
    acquisition_source: nullableString(record.acquisition_source),
    acquisition_at: nullableString(record.acquisition_at),
    acquisition_event_id: nullableString(record.acquisition_event_id),
    acquisition_schema_version:
      optionalNumberValue(record.acquisition_schema_version) ?? null,
    acquisition_group: nullableString(
      record.acquisition_group,
    ) as Contact["acquisition_group"],
    acquisition_medium: nullableString(record.acquisition_medium),
    acquisition_platform: nullableString(record.acquisition_platform),
    acquisition_surface: nullableString(record.acquisition_surface),
    acquisition_campaign_id: nullableString(record.acquisition_campaign_id),
    acquisition_marker: nullableString(record.acquisition_marker),
    acquisition_confidence: nullableString(
      record.acquisition_confidence,
    ) as Contact["acquisition_confidence"],
    acquisition_classifier_version: nullableString(
      record.acquisition_classifier_version,
    ),
    acquisition_reason: nullableString(record.acquisition_reason),
    preferred_language: nullableString(record.preferred_language),
    preferred_language_source: nullableString(record.preferred_language_source),
    preferred_language_updated_at: nullableString(
      record.preferred_language_updated_at,
    ),
    acquisition_detail:
      record.acquisition_detail && typeof record.acquisition_detail === "object"
        ? (record.acquisition_detail as Record<string, unknown>)
        : null,
    phone_e164: nullableString(record.phone_e164),
    phone_country_calling_code: nullableString(
      record.phone_country_calling_code,
    ),
    phone_country_iso2: nullableString(record.phone_country_iso2),
    phone_country_name: nullableString(record.phone_country_name),
    phone_continent_code: nullableString(record.phone_continent_code),
    phone_continent_name: nullableString(record.phone_continent_name),
    phone_geo_status: nullableString(record.phone_geo_status),
    phone_geo_source: nullableString(record.phone_geo_source),
    phone_geo_metadata_version: nullableString(
      record.phone_geo_metadata_version,
    ),
    phone_geo_updated_at: nullableString(record.phone_geo_updated_at),
    i2v_cost_total_usd: optionalNumberValue(record.i2v_cost_total_usd) ?? null,
    i2v_cost_estimated_total_usd:
      optionalNumberValue(record.i2v_cost_estimated_total_usd) ?? null,
    i2v_cost_provider_reported_total_usd:
      optionalNumberValue(record.i2v_cost_provider_reported_total_usd) ?? null,
    i2v_cost_echo_count:
      optionalNumberValue(record.i2v_cost_echo_count) ?? null,
    i2v_cost_unknown_echo_count:
      optionalNumberValue(record.i2v_cost_unknown_echo_count) ?? null,
    i2v_cost_line_item_count:
      optionalNumberValue(record.i2v_cost_line_item_count) ?? null,
    i2v_cost_unknown_count:
      optionalNumberValue(record.i2v_cost_unknown_count) ?? null,
    i2v_cost_by_provider: moneyMap(record.i2v_cost_by_provider),
    i2v_cost_by_concrete_provider: moneyMap(
      record.i2v_cost_by_concrete_provider,
    ),
    i2v_cost_by_provider_tier: moneyMap(record.i2v_cost_by_provider_tier),
    i2v_cost_by_model: moneyMap(record.i2v_cost_by_model),
    i2v_cost_by_segment: moneyMap(record.i2v_cost_by_segment),
    i2v_cost_first_at: nullableString(record.i2v_cost_first_at),
    i2v_cost_last_at: nullableString(record.i2v_cost_last_at),
    i2v_cost_updated_at: nullableString(record.i2v_cost_updated_at),
  } satisfies Contact;
}

function mapTemplate(record: Record<string, unknown>): WhatsAppTemplate {
  const id = stringValue(record.id ?? record.template_id ?? record.name);
  return {
    ...record,
    id,
    template_id: stringValue(record.template_id ?? id),
    meta_template_id: nullableString(record.meta_template_id),
    provider_template_id: nullableString(record.provider_template_id),
    waba_id: nullableString(record.waba_id),
    provider_waba_id: nullableString(record.provider_waba_id),
    sender_environment: nullableString(record.sender_environment),
    sender_phone_number_id: nullableString(record.sender_phone_number_id),
    library_template_name: nullableString(record.library_template_name),
    latest_import_id: nullableString(record.latest_import_id),
    name: stringValue(record.name),
    language: stringValue(record.language ?? "en_US"),
    category: stringValue(record.category ?? "UTILITY"),
    status: stringValue(record.status ?? "draft"),
    quality_score: nullableString(record.quality_score),
    body_text: nullableString(record.body_text),
    header_format: nullableString(record.header_format),
    header_text: nullableString(record.header_text),
    header_handle: nullableString(record.header_handle),
    header_media_asset_id: nullableString(record.header_media_asset_id),
    header_media_url: nullableString(record.header_media_url),
    header_media_mime_type: nullableString(record.header_media_mime_type),
    header_media_file_name: nullableString(record.header_media_file_name),
    header_example: nullableString(record.header_example),
    body_examples: Array.isArray(record.body_examples)
      ? (record.body_examples as WhatsAppTemplate["body_examples"])
      : null,
    footer_text: nullableString(record.footer_text),
    buttons: Array.isArray(record.buttons) ? record.buttons : null,
    components: Array.isArray(record.components) ? record.components : null,
    parameter_schema: Array.isArray(record.parameter_schema)
      ? (record.parameter_schema as WhatsAppTemplate["parameter_schema"])
      : [],
    rejection_reason: nullableTemplateReason(
      record.rejection_reason ?? record.rejected_reason,
    ),
    paused_reason: nullableString(record.paused_reason),
    appeal_status: nullableString(record.appeal_status),
    appeal_notes: Array.isArray(record.appeal_notes)
      ? (record.appeal_notes as WhatsAppTemplate["appeal_notes"])
      : [],
    send_eligible: Boolean(record.send_eligible),
    broadcast_eligible: Boolean(record.broadcast_eligible),
    source: nullableString(record.source),
    created_at: nullableString(record.created_at),
    updated_at: nullableString(record.updated_at),
    submitted_at: nullableString(record.submitted_at),
    last_synced_at: nullableString(record.last_synced_at),
    sync_status: nullableString(record.sync_status),
    fixed_content: Boolean(record.fixed_content),
    template_status_source: nullableString(record.template_status_source),
  } satisfies WhatsAppTemplate;
}

function mapTemplateLibraryItem(
  record: Record<string, unknown>,
): WhatsAppTemplateLibraryItem {
  const id = stringValue(
    record.id ??
      record.template_id ??
      record.provider_template_id ??
      record.library_template_name ??
      record.name,
  );
  return {
    ...record,
    id,
    template_id: stringValue(record.template_id ?? id),
    provider_template_id: nullableString(record.provider_template_id),
    library_template_name: stringValue(
      record.library_template_name ?? record.name ?? id,
    ),
    name: stringValue(record.name ?? record.library_template_name ?? id),
    language: stringValue(record.language ?? "en_US"),
    category: stringValue(record.category ?? "UTILITY"),
    body_text: nullableString(record.body_text),
    body_examples: Array.isArray(record.body_examples)
      ? (record.body_examples as WhatsAppTemplateLibraryItem["body_examples"])
      : null,
    header_text: nullableString(record.header_text),
    buttons: Array.isArray(record.buttons) ? record.buttons : null,
    topic: nullableString(record.topic),
    usecase: nullableString(record.usecase),
    industry:
      Array.isArray(record.industry) || typeof record.industry === "string"
        ? (record.industry as WhatsAppTemplateLibraryItem["industry"])
        : null,
    fixed_content: Boolean(record.fixed_content),
    source: nullableString(record.source),
    parameter_schema: Array.isArray(record.parameter_schema)
      ? (record.parameter_schema as WhatsAppTemplateLibraryItem["parameter_schema"])
      : [],
    raw: isRecord(record.raw) ? record.raw : record,
  } satisfies WhatsAppTemplateLibraryItem;
}

function mapTemplateLibraryImport(
  record: Record<string, unknown>,
): WhatsAppTemplateLibraryImport {
  const id = stringValue(record.id ?? record.import_id);
  return {
    ...record,
    id,
    import_id: stringValue(record.import_id ?? id),
    template_id: nullableString(record.template_id),
    meta_template_id: nullableString(record.meta_template_id),
    template_name: stringValue(record.template_name),
    template_language: stringValue(record.template_language ?? "en_US"),
    template_category: nullableString(record.template_category),
    template_status: stringValue(record.template_status ?? "pending"),
    template_quality_score: nullableString(record.template_quality_score),
    library_template_name: stringValue(record.library_template_name),
    library_template_snapshot: isRecord(record.library_template_snapshot)
      ? record.library_template_snapshot
      : null,
    library_template_body_inputs: isRecord(record.library_template_body_inputs)
      ? record.library_template_body_inputs
      : null,
    library_template_button_inputs: Array.isArray(
      record.library_template_button_inputs,
    )
      ? record.library_template_button_inputs
      : null,
    provider: nullableString(record.provider),
    provider_waba_id: nullableString(record.provider_waba_id),
    provider_region: nullableString(record.provider_region),
    sync_status: nullableString(record.sync_status),
    last_synced_at: nullableString(record.last_synced_at),
    operator_user_id: nullableString(record.operator_user_id),
    operator_name: nullableString(record.operator_name),
    operator_identity_status: nullableString(record.operator_identity_status),
    created_at: nullableString(record.created_at),
    updated_at: nullableString(record.updated_at),
  } satisfies WhatsAppTemplateLibraryImport;
}

function mapTemplateMedia(
  record: Record<string, unknown>,
): WhatsAppTemplateMediaAsset {
  const id = stringValue(record.id ?? record.asset_id);
  return {
    ...record,
    id,
    asset_id: stringValue(record.asset_id ?? id),
    file_name: stringValue(record.file_name),
    mime_type: stringValue(record.mime_type),
    size_bytes: numberValue(record.size_bytes),
    storage_bucket: nullableString(record.storage_bucket ?? record.bucket),
    storage_path: nullableString(record.storage_path ?? record.key),
    provider_media_id: nullableString(record.provider_media_id),
    header_handle: nullableString(record.header_handle),
    sample_handle: nullableString(record.sample_handle),
    preview_url: nullableString(record.preview_url),
    target_header_format: nullableString(record.target_header_format),
    source_file_name: nullableString(record.source_file_name),
    source_mime_type: nullableString(record.source_mime_type),
    source_storage_path: nullableString(record.source_storage_path),
    render_status: nullableString(record.render_status),
    upload_strategy: nullableString(record.upload_strategy),
    created_at: nullableString(record.created_at),
  } satisfies WhatsAppTemplateMediaAsset;
}

function mapBroadcast(record: Record<string, unknown>): WhatsAppBroadcast {
  const id = stringValue(record.id ?? record.broadcast_id);
  return {
    ...record,
    id,
    broadcast_id: stringValue(record.broadcast_id ?? id),
    name: stringValue(record.name),
    template_id: stringValue(record.template_id || ""),
    template_name: stringValue(record.template_name),
    template_language: stringValue(record.template_language ?? "en_US"),
    template_category: nullableString(record.template_category),
    status: stringValue(record.status ?? "queued"),
    audience_status: nullableString(record.audience_status),
    send_when_ready:
      typeof record.send_when_ready === "boolean"
        ? record.send_when_ready
        : null,
    scheduled_for: nullableString(record.scheduled_for),
    timezone: nullableString(record.timezone),
    audience_source: nullableString(record.audience_source),
    operator_compliance_attestation: nullableString(
      record.operator_compliance_attestation,
    ),
    recipient_count: numberValue(record.recipient_count),
    eligible_count: numberValue(record.eligible_count),
    skipped_count: numberValue(record.skipped_count),
    queued_count: numberValue(record.queued_count),
    sending_count: numberValue(record.sending_count),
    accepted_count: numberValue(record.accepted_count),
    held_count: numberValue(record.held_count),
    sent_count: numberValue(record.sent_count),
    delivered_count: numberValue(record.delivered_count),
    read_count: numberValue(record.read_count),
    failed_count: numberValue(record.failed_count),
    cancelled_count: numberValue(record.cancelled_count),
    inbound_count: numberValue(record.inbound_count),
    create_command_count: numberValue(record.create_command_count),
    buy_count: numberValue(record.buy_count),
    echo_started_count: numberValue(record.echo_started_count),
    generation_requested_count: numberValue(record.generation_requested_count),
    final_video_created_count: numberValue(record.final_video_created_count),
    final_video_delivered_count: numberValue(
      record.final_video_delivered_count,
    ),
    ignored_delivered_unread_count: numberValue(
      record.ignored_delivered_unread_count,
    ),
    ignored_read_no_action_count: numberValue(
      record.ignored_read_no_action_count,
    ),
    ignored_no_conversion_count: numberValue(
      record.ignored_no_conversion_count,
    ),
    delivery_rate: numberValue(record.delivery_rate),
    read_rate: numberValue(record.read_rate),
    create_rate: numberValue(record.create_rate),
    skip_reasons: (record.skip_reasons as Record<string, number>) ?? {},
    created_at: stringValue(record.created_at),
    updated_at: nullableString(record.updated_at),
    completed_at: nullableString(record.completed_at),
    created_by_user_id: nullableString(record.created_by_user_id),
    created_by_name: nullableString(record.created_by_name),
    audience_filter_snapshot:
      (record.audience_filter_snapshot as Record<string, unknown>) ?? null,
    audience_resolution_stats:
      (record.audience_resolution_stats as WhatsAppBroadcast["audience_resolution_stats"]) ??
      null,
    audience_selection:
      (record.audience_selection as WhatsAppBroadcast["audience_selection"]) ??
      null,
    default_template_parameters:
      (record.default_template_parameters as Record<string, string>) ?? null,
    template_parameters_by_contact:
      (record.template_parameters_by_contact as Record<
        string,
        Record<string, string>
      >) ?? null,
    header_media_asset_id: nullableString(record.header_media_asset_id),
    header_media_source: nullableString(record.header_media_source),
    include_not_opted_in:
      typeof record.include_not_opted_in === "boolean"
        ? record.include_not_opted_in
        : null,
    edited_at: nullableString(record.edited_at),
    edit_count: numberValue(record.edit_count),
    last_edited_by_user_id: nullableString(record.last_edited_by_user_id),
    last_edited_by_name: nullableString(record.last_edited_by_name),
  } satisfies WhatsAppBroadcast;
}

function mapBroadcastRecipient(
  record: Record<string, unknown>,
): WhatsAppBroadcastRecipient {
  const id = stringValue(record.id);
  return {
    ...record,
    id,
    broadcast_id: stringValue(record.broadcast_id),
    contact_id: stringValue(record.contact_id),
    contact_name: nullableString(record.contact_name),
    whatsapp_user_id: nullableString(record.whatsapp_user_id),
    status: stringValue(record.status ?? record.provider_status),
    provider_status: nullableString(record.provider_status),
    provider_message_id: nullableString(record.provider_message_id),
    outbound_id: nullableString(record.outbound_id),
    skip_reasons: Array.isArray(record.skip_reasons)
      ? (record.skip_reasons as string[])
      : [],
    provider_error_code: nullableString(record.provider_error_code),
    provider_error_title: nullableString(record.provider_error_title),
    provider_error_details: nullableString(record.provider_error_details),
    sent_at: nullableString(record.sent_at),
    accepted_at: nullableString(record.accepted_at),
    first_sent_at: nullableString(record.first_sent_at),
    first_delivered_at: nullableString(record.first_delivered_at),
    first_read_at: nullableString(record.first_read_at),
    first_failed_at: nullableString(record.first_failed_at),
    first_inbound_after_broadcast_at: nullableString(
      record.first_inbound_after_broadcast_at,
    ),
    first_create_command_at: nullableString(record.first_create_command_at),
    first_echo_started_at: nullableString(record.first_echo_started_at),
    first_generation_requested_at: nullableString(
      record.first_generation_requested_at,
    ),
    first_final_video_created_at: nullableString(
      record.first_final_video_created_at,
    ),
    first_final_video_delivered_at: nullableString(
      record.first_final_video_delivered_at,
    ),
    engagement_state: nullableString(record.engagement_state),
    ignored_state: nullableString(record.ignored_state),
    attribution_confidence: nullableString(record.attribution_confidence),
    reply_text: nullableString(record.reply_text),
    reply_context_message_id: nullableString(record.reply_context_message_id),
    conversation_deep_link: nullableString(record.conversation_deep_link),
    pricing_category: nullableString(record.pricing_category),
    pricing_model: nullableString(record.pricing_model),
    pricing_type: nullableString(record.pricing_type),
    billable: nullableBoolean(record.billable),
    status_updated_at: nullableString(record.status_updated_at),
    created_at: stringValue(record.created_at),
    updated_at: nullableString(record.updated_at),
    phone_country_calling_code: nullableString(
      record.phone_country_calling_code,
    ),
    phone_country_iso2: nullableString(record.phone_country_iso2),
    phone_country_name: nullableString(record.phone_country_name),
    phone_continent_code: nullableString(record.phone_continent_code),
    phone_continent_name: nullableString(record.phone_continent_name),
    phone_geo_status: nullableString(record.phone_geo_status),
  } satisfies WhatsAppBroadcastRecipient;
}

function mapBroadcastAnalytics(
  record: Record<string, unknown>,
  warnings: WhatsAppBroadcastAnalytics["warnings"] = [],
): WhatsAppBroadcastAnalytics {
  const id = stringValue(record.id ?? record.broadcast_id);
  return {
    ...record,
    id,
    broadcast_id: stringValue(record.broadcast_id ?? id),
    template_name: nullableString(record.template_name),
    template_language: nullableString(record.template_language),
    recipient_count: numberValue(record.recipient_count),
    eligible_count: numberValue(record.eligible_count),
    skipped_count: numberValue(record.skipped_count),
    queued_count: numberValue(record.queued_count),
    sending_count: numberValue(record.sending_count),
    accepted_count: numberValue(record.accepted_count),
    held_count: numberValue(record.held_count),
    sent_count: numberValue(record.sent_count),
    delivered_count: numberValue(record.delivered_count),
    read_count: numberValue(record.read_count),
    failed_count: numberValue(record.failed_count),
    cancelled_count: numberValue(record.cancelled_count),
    inbound_count: numberValue(record.inbound_count),
    create_command_count: numberValue(record.create_command_count),
    buy_count: numberValue(record.buy_count),
    echo_started_count: numberValue(record.echo_started_count),
    generation_requested_count: numberValue(record.generation_requested_count),
    final_video_created_count: numberValue(record.final_video_created_count),
    final_video_delivered_count: numberValue(
      record.final_video_delivered_count,
    ),
    ignored_delivered_unread_count: numberValue(
      record.ignored_delivered_unread_count,
    ),
    ignored_read_no_action_count: numberValue(
      record.ignored_read_no_action_count,
    ),
    ignored_no_conversion_count: numberValue(
      record.ignored_no_conversion_count,
    ),
    error_count: numberValue(record.error_count),
    delivery_rate: numberValue(record.delivery_rate),
    read_rate: numberValue(record.read_rate),
    create_rate: numberValue(record.create_rate),
    echo_started_rate: numberValue(record.echo_started_rate),
    final_video_delivered_rate: numberValue(record.final_video_delivered_rate),
    attribution_window_hours: numberValue(record.attribution_window_hours),
    total_spend: nullableNumber(record.total_spend),
    cost_per_delivered: nullableNumber(record.cost_per_delivered),
    cost_per_reply: nullableNumber(record.cost_per_reply),
    billable_message_count: nullableNumber(record.billable_message_count),
    currency: nullableString(record.currency),
    template_clicked_url_button: nullableNumber(
      record.template_clicked_url_button,
    ),
    template_clicked_quick_reply: nullableNumber(
      record.template_clicked_quick_reply,
    ),
    template_analytics_available: Boolean(record.template_analytics_available),
    template_analytics_unavailable_reason: nullableString(
      record.template_analytics_unavailable_reason,
    ),
    updated_at: nullableString(record.updated_at),
    last_recomputed_at: nullableString(record.last_recomputed_at),
    warnings,
  } satisfies WhatsAppBroadcastAnalytics;
}

function mapJourney(record: Record<string, unknown>): EchoflicksSessionJourney {
  const sessionId = stringValue(record.session_id ?? record.id);
  const nodes = Array.isArray(record.nodes)
    ? record.nodes.map((node) => mapJourneyNode(node))
    : [];
  const edges = Array.isArray(record.edges)
    ? record.edges.map((edge) => mapJourneyEdge(edge))
    : [];
  const i2vPromptAttempts = Array.isArray(record.i2v_prompt_attempts)
    ? record.i2v_prompt_attempts.map((attempt) => mapI2VPromptAttempt(attempt))
    : [];
  const i2vPromptAuthorings = Array.isArray(record.i2v_prompt_authorings)
    ? record.i2v_prompt_authorings.map((authoring) =>
        mapI2VPromptAuthoring(authoring),
      )
    : [];
  const finalVideoDeliveries = Array.isArray(record.final_video_deliveries)
    ? record.final_video_deliveries.map((delivery) =>
        mapFinalVideoDelivery(delivery),
      )
    : [];
  const framing = Array.isArray(record.framing)
    ? record.framing.map((event) => mapFramingEvent(event))
    : [];

  return {
    ...record,
    id: sessionId,
    session_id: sessionId,
    workload_id: nullableString(record.workload_id),
    summary: mapJourneySummary(record.summary),
    nodes,
    edges,
    i2v_prompt_attempts: i2vPromptAttempts,
    i2v_prompt_authorings: i2vPromptAuthorings,
    i2v_prompt_director: mapI2VPromptDirectorSummary(
      record.i2v_prompt_director,
    ),
    final_video_deliveries: finalVideoDeliveries,
    i2v_prompt_telemetry_state: nullableString(
      record.i2v_prompt_telemetry_state,
    ) as EchoflicksSessionJourney["i2v_prompt_telemetry_state"],
    framing,
  } satisfies EchoflicksSessionJourney;
}

function mapFramingEvent(value: unknown): EchoflicksSessionFramingEvent {
  const record = isRecord(value) ? value : {};
  const subjects = Array.isArray(record.subject_summary)
    ? record.subject_summary.filter(isRecord).map((subject) => ({
        subject_id: nullableString(subject.subject_id),
        label: nullableString(subject.label),
        kind: nullableString(subject.kind),
        priority: nullableString(subject.priority),
        confidence: nullableNumber(subject.confidence),
        detector: nullableString(subject.detector),
        coordinate_space_id: nullableString(subject.coordinate_space_id),
        identity_required: nullableBoolean(subject.identity_required),
      }))
    : [];
  const rawQc = isRecord(record.qc_summary) ? record.qc_summary : null;
  const metrics = rawQc && isRecord(rawQc.metrics) ? rawQc.metrics : null;
  return {
    ...record,
    id: stringValue(record.id),
    session_id: stringValue(record.session_id),
    event_type: stringValue(record.event_type),
    scene_id:
      typeof record.scene_id === "number" || typeof record.scene_id === "string"
        ? record.scene_id
        : null,
    schema_version: nullableString(record.schema_version),
    framing_plan_id: nullableString(record.framing_plan_id),
    plan_digest: nullableString(record.plan_digest),
    plan_revision: optionalNumberValue(record.plan_revision) ?? null,
    attempt_id: nullableString(record.attempt_id),
    task_id: nullableString(record.task_id),
    receipt_id: nullableString(record.receipt_id),
    config_release_id: nullableString(record.config_release_id),
    config_digest: nullableString(record.config_digest),
    config_schema_version: nullableString(record.config_schema_version),
    intent_source: nullableString(record.intent_source),
    strategy: nullableString(record.strategy),
    subject_summary: subjects,
    qc_summary: rawQc
      ? {
          status: nullableString(rawQc.status),
          issue_codes: mapStringList(rawQc.issue_codes),
          metrics: metrics as Record<
            string,
            number | string | boolean | null
          > | null,
          checked_coordinate_space_id: nullableString(
            rawQc.checked_coordinate_space_id ?? rawQc.checked_space_id,
          ),
          checked_space_id: nullableString(
            rawQc.checked_space_id ?? rawQc.checked_coordinate_space_id,
          ),
          sample_count: optionalNumberValue(rawQc.sample_count) ?? null,
          repair_attempt: optionalNumberValue(rawQc.repair_attempt) ?? null,
        }
      : null,
    repair_summary: isRecord(record.repair_summary)
      ? record.repair_summary
      : null,
    fallback_reason: nullableString(record.fallback_reason),
    provider_aspect: nullableString(record.provider_aspect),
    delivery_aspect: nullableString(record.delivery_aspect),
    artifact_refs: Array.isArray(record.artifact_refs)
      ? record.artifact_refs.filter(isRecord)
      : [],
    report_storage_bucket: nullableString(record.report_storage_bucket),
    report_storage_path: nullableString(record.report_storage_path),
    payload: isRecord(record.payload) ? record.payload : null,
    occurred_at: nullableString(record.occurred_at),
    source: nullableString(record.source),
  };
}

function mapI2VPromptAttempt(value: unknown): I2VPromptAttempt {
  const record = isRecord(value) ? value : {};
  const auditId = stringValue(record.audit_id ?? record.id);
  return {
    ...record,
    id: auditId,
    audit_id: auditId,
    schema_version: nullableString(record.schema_version),
    session_id: nullableString(record.session_id),
    workload_id: nullableString(record.workload_id),
    scene_id:
      typeof record.scene_id === "number" || typeof record.scene_id === "string"
        ? record.scene_id
        : nullableString(record.scene_id),
    attempt: optionalNumberValue(record.attempt) ?? null,
    max_attempts: optionalNumberValue(record.max_attempts) ?? null,
    status: nullableString(record.status),
    terminal: nullableBoolean(record.terminal),
    provider: nullableString(record.provider),
    concrete_provider: nullableString(record.concrete_provider),
    model: nullableString(record.model),
    task_id: nullableString(record.task_id),
    elapsed_ms: optionalNumberValue(record.elapsed_ms) ?? null,
    queue_wait_ms: optionalNumberValue(record.queue_wait_ms) ?? null,
    error_code: nullableString(record.error_code),
    error_message: nullableString(record.error_message),
    retryable: nullableBoolean(record.retryable),
    fallback: nullableBoolean(record.fallback),
    fallback_from: nullableString(record.fallback_from),
    asset_s3_key: nullableString(record.asset_s3_key),
    resolved_at: nullableString(record.resolved_at),
    submitted_at: nullableString(record.submitted_at),
    finished_at: nullableString(record.finished_at),
    route_contract_version: nullableString(record.route_contract_version),
    route_contract_digest: nullableString(record.route_contract_digest),
    scene_revision_id: nullableString(record.scene_revision_id),
    packet_id: nullableString(record.packet_id),
    packet_digest: nullableString(record.packet_digest),
    provider_request_digest: nullableString(record.provider_request_digest),
    idempotency_key: nullableString(record.idempotency_key),
    semantic_attempt_ordinal:
      optionalNumberValue(record.semantic_attempt_ordinal) ?? null,
    transport_try_index:
      optionalNumberValue(record.transport_try_index) ?? null,
    parent_attempt_id: nullableString(record.parent_attempt_id),
    patch_id: nullableString(record.patch_id),
    base_packet_digest: nullableString(record.base_packet_digest),
    flow_id: nullableString(record.flow_id),
    config_release_id: nullableString(record.config_release_id),
    config_release_digest: nullableString(record.config_release_digest),
    prompt_bundle_id: nullableString(record.prompt_bundle_id),
    prompt_bundle_digest: nullableString(record.prompt_bundle_digest),
    template_id: nullableString(record.template_id),
    template_version: nullableString(record.template_version),
    template_digest: nullableString(record.template_digest),
    transformation_records: Array.isArray(record.transformation_records)
      ? record.transformation_records.filter(isRecord)
      : [],
    capability_decisions: Array.isArray(record.capability_decisions)
      ? record.capability_decisions.filter(isRecord)
      : [],
    capability_warnings: Array.isArray(record.capability_warnings)
      ? record.capability_warnings.filter(isRecord)
      : [],
    render_spec_fingerprint: nullableString(record.render_spec_fingerprint),
    reuse_status: nullableString(record.reuse_status),
    reuse_reason: nullableString(record.reuse_reason),
    requested_model: nullableString(record.requested_model),
    reported_model: nullableString(record.reported_model),
    protected_event_id: nullableString(record.protected_event_id),
    projection_conflicts: mapStringList(record.projection_conflicts),
    prompt_audit_summary: mapI2VPromptAuditSummary(record.prompt_audit_summary),
  };
}

function mapI2VPromptAuthoring(value: unknown): I2VPromptAuthoring {
  const record = isRecord(value) ? value : {};
  const auditId = stringValue(record.audit_id ?? record.id);
  return {
    ...record,
    id: auditId,
    audit_id: auditId,
    schema_version: nullableString(record.schema_version),
    authoring_run_id: nullableString(record.authoring_run_id),
    scene_id:
      typeof record.scene_id === "number" || typeof record.scene_id === "string"
        ? record.scene_id
        : null,
    scene_position: optionalNumberValue(record.scene_position) ?? null,
    previous_scene_id:
      typeof record.previous_scene_id === "number" ||
      typeof record.previous_scene_id === "string"
        ? record.previous_scene_id
        : null,
    next_scene_id:
      typeof record.next_scene_id === "number" ||
      typeof record.next_scene_id === "string"
        ? record.next_scene_id
        : null,
    source_file: nullableString(record.source_file),
    source_media_id: nullableString(record.source_media_id),
    source_storage_key: nullableString(record.source_storage_key),
    source_image_sha256: nullableString(record.source_image_sha256),
    source_width: optionalNumberValue(record.source_width) ?? null,
    source_height: optionalNumberValue(record.source_height) ?? null,
    target_key: nullableString(record.target_key),
    target_scope: nullableString(record.target_scope),
    requested_model: nullableString(record.requested_model),
    route_role: nullableString(record.route_role),
    scene_revision_id: nullableString(record.scene_revision_id),
    prompt_revision_id: nullableString(record.prompt_revision_id),
    config_release_id: nullableString(record.config_release_id),
    config_release_digest: nullableString(record.config_release_digest),
    route_contract_version: nullableString(record.route_contract_version),
    route_contract_digest: nullableString(record.route_contract_digest),
    prompt_bundle_id: nullableString(record.prompt_bundle_id),
    prompt_bundle_digest: nullableString(record.prompt_bundle_digest),
    template_id: nullableString(record.template_id),
    template_version: nullableString(record.template_version),
    template_digest: nullableString(record.template_digest),
    status: nullableString(record.status),
    terminal: nullableBoolean(record.terminal),
    prompt: nullableString(record.prompt),
    prompt_sha256: nullableString(record.prompt_sha256),
    prompt_truncated: nullableBoolean(record.prompt_truncated),
    guide_version: nullableString(record.guide_version),
    guide_digest: nullableString(record.guide_digest),
    vision_digest: nullableString(record.vision_digest),
    shot_list_digest: nullableString(record.shot_list_digest),
    decision: isRecord(record.decision) ? record.decision : null,
    attempts: Array.isArray(record.attempts)
      ? record.attempts.filter(isRecord).map((attempt) => ({
          ordinal: optionalNumberValue(attempt.ordinal) ?? null,
          status: nullableString(attempt.status),
          started_at: nullableString(attempt.started_at),
          elapsed_ms: optionalNumberValue(attempt.elapsed_ms) ?? null,
          semaphore_wait_ms:
            optionalNumberValue(attempt.semaphore_wait_ms) ?? null,
          requested_model: nullableString(attempt.requested_model),
          observed_model: nullableString(attempt.observed_model),
          observed_provider: nullableString(attempt.observed_provider),
          input_tokens: optionalNumberValue(attempt.input_tokens) ?? null,
          output_tokens: optionalNumberValue(attempt.output_tokens) ?? null,
          usage_source: nullableString(attempt.usage_source),
          error_code: nullableString(attempt.error_code),
          error_message: nullableString(attempt.error_message),
          retryable: nullableBoolean(attempt.retryable),
        }))
      : [],
    lock_compliant: nullableBoolean(record.lock_compliant),
    locked_contract_hash: nullableString(record.locked_contract_hash),
    error_code: nullableString(record.error_code),
    error_message: nullableString(record.error_message),
    redaction_state: nullableString(record.redaction_state),
    projection_state: nullableString(record.projection_state),
    projection_warning: nullableString(record.projection_warning),
    occurred_at: nullableString(record.occurred_at),
    updated_at: nullableString(record.updated_at),
    projection_conflicts: mapStringList(record.projection_conflicts),
  };
}

function mapI2VPromptDirectorSummary(
  value: unknown,
): I2VPromptDirectorSummary | null {
  const record = isRecord(value) ? value : null;
  if (!record) return null;
  return {
    id: nullableString(record.id),
    schema_version: nullableString(record.schema_version),
    authoring_run_id: nullableString(record.authoring_run_id),
    status: nullableString(record.status),
    authoring_mode: nullableString(record.authoring_mode),
    scene_count: optionalNumberValue(record.scene_count) ?? null,
    images_attached: optionalNumberValue(record.images_attached) ?? null,
    attachment_complete: nullableBoolean(record.attachment_complete),
    authored_scene_count:
      optionalNumberValue(record.authored_scene_count) ?? null,
    resolved_scene_count:
      optionalNumberValue(record.resolved_scene_count) ?? null,
    resolved_outcome_count:
      optionalNumberValue(record.resolved_outcome_count) ?? null,
    copied_legacy_count:
      optionalNumberValue(record.copied_legacy_count) ?? null,
    copied_legacy_scene_count:
      optionalNumberValue(record.copied_legacy_scene_count) ?? null,
    authoring_record_count:
      optionalNumberValue(record.authoring_record_count) ?? null,
    terminal_outcome_count:
      optionalNumberValue(record.terminal_outcome_count) ?? null,
    primary_scene_count:
      optionalNumberValue(record.primary_scene_count) ?? null,
    secondary_scene_count:
      optionalNumberValue(record.secondary_scene_count) ?? null,
    target_keys: mapStringList(record.target_keys),
    authored_counts: isRecord(record.authored_counts)
      ? (record.authored_counts as Record<string, number>)
      : null,
    missing_images: Array.isArray(record.missing_images)
      ? record.missing_images.filter(isRecord)
      : [],
    failed_keys: mapStringList(record.failed_keys),
    authoring_budget: isRecord(record.authoring_budget)
      ? record.authoring_budget
      : null,
    vision_digest: nullableString(record.vision_digest),
    shot_list_digest: nullableString(record.shot_list_digest),
    route_contract: isRecord(record.route_contract)
      ? record.route_contract
      : null,
    config_release_id: nullableString(record.config_release_id),
    config_release_digest: nullableString(record.config_release_digest),
    route_contract_version: nullableString(record.route_contract_version),
    route_contract_digest: nullableString(record.route_contract_digest),
    prompt_bundle_id: nullableString(record.prompt_bundle_id),
    prompt_bundle_digest: nullableString(record.prompt_bundle_digest),
    template_id: nullableString(record.template_id),
    template_version: nullableString(record.template_version),
    template_digest: nullableString(record.template_digest),
    error_code: nullableString(record.error_code),
    occurred_at: nullableString(record.occurred_at),
    updated_at: nullableString(record.updated_at),
  };
}

function mapFinalVideoDelivery(value: unknown): FinalVideoDelivery {
  const record = isRecord(value) ? value : {};
  const deliveryId = stringValue(record.delivery_id ?? record.id);
  return {
    ...record,
    id: deliveryId,
    delivery_id: deliveryId,
    workload_id: nullableString(record.workload_id),
    asset_identity: nullableString(record.asset_identity),
    channel: nullableString(record.channel),
    status: nullableString(record.status),
    terminal: nullableBoolean(record.terminal),
    method: nullableString(record.method),
    provider_message_id: nullableString(record.provider_message_id),
    failure_reason: nullableString(record.failure_reason),
    reconciliation_required: nullableBoolean(record.reconciliation_required),
    reconciled_at: nullableString(record.reconciled_at),
    reconciled_by: nullableString(record.reconciled_by),
    reconciliation_reason: nullableString(record.reconciliation_reason),
    reconciliation_history: Array.isArray(record.reconciliation_history)
      ? record.reconciliation_history.flatMap((value) => {
          const reconciliation = isRecord(value) ? value : null;
          const actor = nullableString(reconciliation?.actor);
          const reason = nullableString(reconciliation?.reason);
          const occurredAt = nullableString(reconciliation?.occurred_at);
          const resolvedStatus = nullableString(
            reconciliation?.resolved_status,
          );
          if (
            !actor ||
            !reason ||
            !occurredAt ||
            !["delivered", "failed"].includes(resolvedStatus ?? "")
          ) {
            return [];
          }
          return [
            {
              event_id: nullableString(reconciliation?.event_id),
              actor,
              reason,
              previous_status: "outcome_unknown" as const,
              resolved_status: resolvedStatus as "delivered" | "failed",
              occurred_at: occurredAt,
            },
          ];
        })
      : [],
    duplicate_suppressed_count:
      optionalNumberValue(record.duplicate_suppressed_count) ?? null,
    final_asset_s3_key: nullableString(record.final_asset_s3_key),
    occurred_at: nullableString(record.occurred_at),
    updated_at: nullableString(record.updated_at),
    lifecycle_updated_at: nullableString(record.lifecycle_updated_at),
    projection_conflicts: mapStringList(record.projection_conflicts),
    outcome: isRecord(record.outcome) ? record.outcome : null,
  };
}

function mapJourneyNode(value: unknown): EchoJourneyNode {
  const record = isRecord(value) ? value : {};
  const rawPayload = isRecord(record.raw_payload) ? record.raw_payload : {};
  return {
    ...record,
    id: stringValue(record.id),
    session_id: nullableString(record.session_id),
    workload_id: nullableString(record.workload_id),
    whatsapp_user_id: nullableString(record.whatsapp_user_id),
    type: stringValue(record.type ?? "journey.node"),
    stage: stringValue(record.stage ?? "conversation"),
    stage_order: optionalNumberValue(record.stage_order),
    label: stringValue(record.label ?? record.type ?? "Journey node"),
    status: stringValue(record.status ?? "succeeded"),
    occurred_at: nullableString(record.occurred_at),
    completed_at: nullableString(record.completed_at),
    service: nullableString(record.service),
    decision: isRecord(record.decision) ? record.decision : null,
    i2v_prompt_audit_summary: mapI2VPromptAuditSummary(
      record.i2v_prompt_audit_summary ?? rawPayload.i2v_prompt_audit_summary,
    ),
    i2v_prompt_audit: mapI2VPromptAudit(
      record.i2v_prompt_audit ?? rawPayload.i2v_prompt_audit,
    ),
    inputs: mapJourneyFields(record.inputs),
    outputs: mapJourneyFields(record.outputs),
    artifacts: mapJourneyFields(record.artifacts),
    timing: mapJourneyTiming(record.timing ?? rawPayload.timing),
    tools: mapJourneyTools(
      record.tools ?? rawPayload.tools ?? rawPayload.tool_calls,
    ),
    duration_impact: mapJourneyDurationImpact(
      record.duration_impact ??
        rawPayload.duration_impact ??
        rawPayload.duration_debug,
    ),
    debug_notes: mapStringList(record.debug_notes ?? rawPayload.debug_notes),
    bottleneck_score: optionalNumberValue(record.bottleneck_score),
    raw_payload: Object.keys(rawPayload).length > 0 ? rawPayload : null,
    confidence: nullableString(record.confidence),
  };
}

function mapJourneyEdge(value: unknown): EchoJourneyEdge {
  const record = isRecord(value) ? value : {};
  return {
    ...record,
    id: stringValue(record.id),
    from: stringValue(record.from),
    to: stringValue(record.to),
    label: nullableString(record.label),
    kind: stringValue(record.kind ?? "normal"),
    confidence: nullableString(record.confidence),
  };
}

function mapJourneyFields(value: unknown): EchoJourneyField[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isRecord).map((record) => ({
    kind: stringValue(record.kind ?? "field"),
    label: stringValue(record.label ?? record.kind ?? "field"),
    value:
      typeof record.value === "string" ||
      typeof record.value === "number" ||
      typeof record.value === "boolean"
        ? record.value
        : nullableString(record.value),
    bucket: nullableString(record.bucket),
    s3_key: nullableString(record.s3_key),
    summary: nullableString(record.summary),
    metadata: isRecord(record.metadata) ? record.metadata : null,
  }));
}

function mapJourneyTiming(value: unknown): EchoJourneyNode["timing"] {
  const record = isRecord(value) ? value : {};
  if (Object.keys(record).length === 0) {
    return null;
  }
  return {
    started_at: nullableString(record.started_at),
    completed_at: nullableString(record.completed_at),
    elapsed_ms: optionalNumberValue(record.elapsed_ms ?? record.latency_ms),
    latency_ms: optionalNumberValue(record.latency_ms ?? record.elapsed_ms),
    queue_ms: optionalNumberValue(record.queue_ms),
    provider_ms: optionalNumberValue(record.provider_ms),
    upload_ms: optionalNumberValue(record.upload_ms),
    retry_count: optionalNumberValue(record.retry_count),
  };
}

function mapJourneyTools(
  value: unknown,
): NonNullable<EchoJourneyNode["tools"]> {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isRecord).map((record) => ({
    name: stringValue(record.name ?? record.tool ?? record.provider ?? "tool"),
    provider: nullableString(record.provider),
    category: nullableString(record.category),
    model: nullableString(record.model),
    action: nullableString(record.action ?? record.operation),
    operation: nullableString(record.operation ?? record.action),
    attempt: optionalNumberValue(record.attempt),
    status: nullableString(record.status),
    elapsed_ms: optionalNumberValue(record.elapsed_ms ?? record.latency_ms),
    latency_ms: optionalNumberValue(record.latency_ms ?? record.elapsed_ms),
    task_id: nullableString(record.task_id ?? record.provider_task_id),
    metadata: isRecord(record.metadata) ? record.metadata : null,
  }));
}

function mapJourneyDurationImpact(
  value: unknown,
): EchoJourneyNode["duration_impact"] {
  const record = isRecord(value) ? value : {};
  if (Object.keys(record).length === 0) {
    return null;
  }
  return {
    scene_id:
      typeof record.scene_id === "number" || typeof record.scene_id === "string"
        ? record.scene_id
        : nullableString(record.scene_id),
    source_scene_type: nullableString(record.source_scene_type),
    final_scene_type: nullableString(record.final_scene_type),
    route: nullableString(record.route),
    provider: nullableString(record.provider),
    target_seconds: optionalNumberValue(record.target_seconds),
    planned_seconds: optionalNumberValue(record.planned_seconds),
    requested_seconds: optionalNumberValue(record.requested_seconds),
    measured_seconds: optionalNumberValue(record.measured_seconds),
    output_seconds: optionalNumberValue(record.output_seconds),
    planned_scene_seconds: optionalNumberValue(
      record.planned_scene_seconds ?? record.planned_seconds,
    ),
    generated_asset_seconds: optionalNumberValue(
      record.generated_asset_seconds ?? record.measured_seconds,
    ),
    target_video_seconds: optionalNumberValue(
      record.target_video_seconds ?? record.target_seconds,
    ),
    expected_video_seconds: optionalNumberValue(
      record.expected_video_seconds ?? record.planned_seconds,
    ),
    actual_video_seconds: optionalNumberValue(
      record.actual_video_seconds ??
        record.output_seconds ??
        record.measured_seconds,
    ),
    moviepy_duration_seconds: optionalNumberValue(
      record.moviepy_duration_seconds,
    ),
    delta_seconds: optionalNumberValue(record.delta_seconds),
    delta_frames: optionalNumberValue(record.delta_frames),
    tolerance_frames: optionalNumberValue(record.tolerance_frames),
    action: nullableString(record.action),
    duration_qc_status: nullableString(
      record.duration_qc_status ?? record.status,
    ),
    fallback_reason: nullableString(record.fallback_reason),
    s3_key: nullableString(record.s3_key),
    reason: nullableString(record.reason),
  };
}

function mapStringList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((item): item is string | number | boolean => {
      return (
        typeof item === "string" ||
        typeof item === "number" ||
        typeof item === "boolean"
      );
    })
    .map(String);
}

function mapJourneySummary(
  value: unknown,
): EchoflicksSessionJourney["summary"] {
  const record = isRecord(value) ? value : {};
  const framing = isRecord(record.framing) ? record.framing : null;
  return {
    status: nullableString(record.status),
    started_at: nullableString(record.started_at),
    completed_at: nullableString(record.completed_at),
    render_created_at: nullableString(record.render_created_at),
    final_video_delivery_status: nullableString(
      record.final_video_delivery_status,
    ),
    final_video_delivery_channel: nullableString(
      record.final_video_delivery_channel,
    ),
    final_video_delivery_method: nullableString(
      record.final_video_delivery_method,
    ),
    final_video_delivery_updated_at: nullableString(
      record.final_video_delivery_updated_at,
    ),
    fallback_count: optionalNumberValue(record.fallback_count),
    failed_node_id: nullableString(record.failed_node_id),
    confidence: nullableString(record.confidence),
    observed_elapsed_ms: optionalNumberValue(record.observed_elapsed_ms),
    target_video_seconds: optionalNumberValue(record.target_video_seconds),
    actual_video_seconds: optionalNumberValue(record.actual_video_seconds),
    framing: framing
      ? {
          status: nullableString(framing.status),
          at_risk: nullableBoolean(framing.at_risk),
          failed_scene_count:
            optionalNumberValue(framing.failed_scene_count) ?? null,
          repaired_scene_count:
            optionalNumberValue(framing.repaired_scene_count) ?? null,
          fallback_scene_count:
            optionalNumberValue(framing.fallback_scene_count) ?? null,
          tracking_scene_count:
            optionalNumberValue(framing.tracking_scene_count) ?? null,
          report_storage_bucket: nullableString(framing.report_storage_bucket),
          report_storage_path: nullableString(framing.report_storage_path),
          latest_at: nullableString(framing.latest_at),
        }
      : null,
  };
}

function mapI2VPromptAuditSummary(
  value: unknown,
): I2VPromptAuditSummary | null {
  const record = isRecord(value) ? value : {};
  if (Object.keys(record).length === 0) {
    return null;
  }
  return {
    schema_version: nullableString(record.schema_version),
    audit_id: nullableString(record.audit_id),
    scene_id:
      typeof record.scene_id === "number" || typeof record.scene_id === "string"
        ? record.scene_id
        : nullableString(record.scene_id),
    source_scene_type: nullableString(record.source_scene_type),
    attempt: optionalNumberValue(record.attempt) ?? null,
    max_attempts: optionalNumberValue(record.max_attempts) ?? null,
    prompt_channel: nullableString(record.prompt_channel),
    route_contract_version: nullableString(record.route_contract_version),
    route_contract_digest: nullableString(record.route_contract_digest),
    provider: nullableString(record.provider),
    route: nullableString(record.route),
    concrete_provider: nullableString(record.concrete_provider),
    model: nullableString(record.model),
    model_version: nullableString(record.model_version),
    mode: nullableString(record.mode),
    task_id: nullableString(record.task_id),
    positive_prompt_sha256: nullableString(record.positive_prompt_sha256),
    negative_prompt_sha256: nullableString(record.negative_prompt_sha256),
    negative_prompt_source: nullableString(record.negative_prompt_source),
    scene_prompt_source: nullableString(record.scene_prompt_source),
    scene_prompt_family: nullableString(record.scene_prompt_family),
    motion_style_enabled: nullableBoolean(record.motion_style_enabled),
    motion_style_applied: nullableBoolean(record.motion_style_applied),
    motion_style_suppressed_reason: nullableString(
      record.motion_style_suppressed_reason,
    ),
    face_sensitive: nullableBoolean(record.face_sensitive),
    face_sensitive_reason: nullableString(record.face_sensitive_reason),
    matched_subject_terms: mapStringList(record.matched_subject_terms),
    matched_expression_terms: mapStringList(record.matched_expression_terms),
    generic_face_terms_enabled: nullableBoolean(
      record.generic_face_terms_enabled,
    ),
    semantic_enforcement_mode: nullableString(record.semantic_enforcement_mode),
    expression_qc_mode: nullableString(record.expression_qc_mode),
    reference_frames: isRecord(record.reference_frames)
      ? record.reference_frames
      : null,
    asset_s3_key: nullableString(record.asset_s3_key),
    asset_disposition: nullableString(record.asset_disposition),
    lifecycle_status: nullableString(record.lifecycle_status),
    lifecycle_terminal: nullableBoolean(record.lifecycle_terminal),
    error_code: nullableString(record.error_code),
    error_message: nullableString(record.error_message),
    retryable: nullableBoolean(record.retryable),
    fallback: nullableBoolean(record.fallback),
    fallback_from: nullableString(record.fallback_from),
    authoring_audit_id: nullableString(record.authoring_audit_id),
    authoring_run_id: nullableString(record.authoring_run_id),
    authoring_target_key: nullableString(record.authoring_target_key),
    authoring_status: nullableString(record.authoring_status),
    authoring_correlation: nullableString(record.authoring_correlation),
    authoring_prompt_sha256: nullableString(record.authoring_prompt_sha256),
    authoring_guide_version: nullableString(record.authoring_guide_version),
    authoring_guide_digest: nullableString(record.authoring_guide_digest),
    authoring_vision_digest: nullableString(record.authoring_vision_digest),
    authoring_shot_list_digest: nullableString(
      record.authoring_shot_list_digest,
    ),
    route_role: nullableString(record.route_role),
    lock_compliant: nullableBoolean(record.lock_compliant),
    scene_revision_id: nullableString(record.scene_revision_id),
    packet_id: nullableString(record.packet_id),
    packet_digest: nullableString(record.packet_digest),
    provider_request_digest: nullableString(record.provider_request_digest),
    idempotency_key: nullableString(record.idempotency_key),
    semantic_attempt_ordinal:
      optionalNumberValue(record.semantic_attempt_ordinal) ?? null,
    transport_try_index:
      optionalNumberValue(record.transport_try_index) ?? null,
    parent_attempt_id: nullableString(record.parent_attempt_id),
    patch_id: nullableString(record.patch_id),
    base_packet_digest: nullableString(record.base_packet_digest),
    flow_id: nullableString(record.flow_id),
    config_release_id: nullableString(record.config_release_id),
    config_release_digest: nullableString(record.config_release_digest),
    prompt_bundle_id: nullableString(record.prompt_bundle_id),
    prompt_bundle_digest: nullableString(record.prompt_bundle_digest),
    template_id: nullableString(record.template_id),
    template_version: nullableString(record.template_version),
    template_digest: nullableString(record.template_digest),
    transformation_records: Array.isArray(record.transformation_records)
      ? record.transformation_records.filter(isRecord)
      : [],
    capability_decisions: Array.isArray(record.capability_decisions)
      ? record.capability_decisions.filter(isRecord)
      : [],
    capability_warnings: Array.isArray(record.capability_warnings)
      ? record.capability_warnings.filter(isRecord)
      : [],
    render_spec_fingerprint: nullableString(record.render_spec_fingerprint),
    reuse_status: nullableString(record.reuse_status),
    reuse_reason: nullableString(record.reuse_reason),
    requested_model: nullableString(record.requested_model),
    reported_model: nullableString(record.reported_model),
  };
}

function mapI2VPromptAudit(value: unknown): I2VPromptAudit | null {
  return isRecord(value) ? (value as I2VPromptAudit) : null;
}

function mapMedia(record: Record<string, unknown>): EchoflicksSessionMedia {
  const s3Key = nullableString(record.s3_key ?? record.storage_path);
  const bucket = nullableString(
    record.storage_bucket ?? record.bucket ?? getDefaultAwsMediaBucket(),
  );
  const auditSummary = mapI2VPromptAuditSummary(
    record.i2v_prompt_audit_summary ??
      (isRecord(record.raw_metadata)
        ? record.raw_metadata.i2v_prompt_audit_summary
        : null),
  );

  return {
    ...record,
    id: stringValue(record.id ?? record.source_id ?? s3Key),
    source_id: stringValue(record.source_id ?? record.id ?? s3Key),
    session_id: nullableString(record.session_id),
    contact_id: nullableString(record.contact_id ?? record.whatsapp_user_id),
    whatsapp_user_id: stringValue(record.whatsapp_user_id ?? "unknown"),
    kind: stringValue(record.kind ?? "generated_asset"),
    media_id: nullableString(record.media_id),
    asset_role: nullableString(record.asset_role),
    bucket,
    s3_key: s3Key,
    storage_bucket: bucket,
    storage_path: s3Key,
    storage_is_public: false,
    mime_type: nullableString(record.mime_type ?? inferMimeType(s3Key)),
    scene_id:
      typeof record.scene_id === "number" || typeof record.scene_id === "string"
        ? record.scene_id
        : nullableString(record.scene_id),
    i2v_prompt_audit_id: nullableString(
      record.i2v_prompt_audit_id ?? auditSummary?.audit_id,
    ),
    prompt_channel: nullableString(
      record.prompt_channel ?? auditSummary?.prompt_channel,
    ),
    provider: nullableString(record.provider ?? auditSummary?.provider),
    concrete_provider: nullableString(
      record.concrete_provider ?? auditSummary?.concrete_provider,
    ),
    model: nullableString(record.model ?? auditSummary?.model),
    model_version: nullableString(
      record.model_version ?? auditSummary?.model_version,
    ),
    task_id: nullableString(record.task_id ?? auditSummary?.task_id),
    positive_prompt_sha256: nullableString(
      record.positive_prompt_sha256 ?? auditSummary?.positive_prompt_sha256,
    ),
    negative_prompt_sha256: nullableString(
      record.negative_prompt_sha256 ?? auditSummary?.negative_prompt_sha256,
    ),
    motion_style_enabled: nullableBoolean(
      record.motion_style_enabled ?? auditSummary?.motion_style_enabled,
    ),
    motion_style_applied: nullableBoolean(
      record.motion_style_applied ?? auditSummary?.motion_style_applied,
    ),
    motion_style_suppressed_reason: nullableString(
      record.motion_style_suppressed_reason ??
        auditSummary?.motion_style_suppressed_reason,
    ),
    face_sensitive: nullableBoolean(
      record.face_sensitive ?? auditSummary?.face_sensitive,
    ),
    face_sensitive_reason: nullableString(
      record.face_sensitive_reason ?? auditSummary?.face_sensitive_reason,
    ),
    semantic_enforcement_mode: nullableString(
      record.semantic_enforcement_mode ??
        auditSummary?.semantic_enforcement_mode,
    ),
    expression_qc_mode: nullableString(
      record.expression_qc_mode ?? auditSummary?.expression_qc_mode,
    ),
    i2v_prompt_audit_summary: auditSummary,
  } satisfies EchoflicksSessionMedia;
}

// Exported for tests (buildTimeline.test.ts).
export function mapMessage(
  record: Record<string, unknown>,
): EchoflicksSessionMessage {
  return {
    ...record,
    id: stringValue(record.id ?? record.source_id),
    source_id: stringValue(record.source_id ?? record.id),
    session_id: nullableString(record.session_id),
    contact_id: nullableString(record.contact_id ?? record.whatsapp_user_id),
    whatsapp_user_id: stringValue(record.whatsapp_user_id ?? "unknown"),
    role: stringValue(record.role ?? "assistant"),
    source: stringValue(record.source ?? "aws-live"),
    source_confidence: stringValue(record.source_confidence ?? "event"),
    // EMC contract C6: delivery status and price of an outbound send. The
    // amounts are Decimals and may arrive as strings.
    provider_message_id: nullableString(record.provider_message_id),
    provider_status: nullableString(record.provider_status),
    pricing_type: nullableString(record.pricing_type),
    pricing_category: nullableString(record.pricing_category),
    cost_usd: optionalNumberValue(record.cost_usd) ?? null,
    cost_eur: optionalNumberValue(record.cost_eur) ?? null,
    billing_state: nullableString(record.billing_state),
    purpose: nullableString(record.purpose),
  } satisfies EchoflicksSessionMessage;
}

/**
 * `echo_cost_acquisition_attribution` (EMC C3.7): a map with `paid` as the
 * string "true" / "false". The plan's earlier wording also allowed the bare
 * string "organic"; both read as an unpaid touch.
 */
function mapAcquisitionAttribution(
  value: unknown,
): EchoAcquisitionAttribution | null {
  if (typeof value === "string") {
    const text = value.trim().toLowerCase();
    return text ? { paid: false, channel: text } : null;
  }
  if (!isRecord(value)) return null;
  return {
    paid: optionalBooleanValue(value.paid),
    channel: nullableString(value.channel),
    source: nullableString(value.source),
    campaign_key: nullableString(value.campaign_key),
    campaign_name: nullableString(value.campaign_name),
    touch_at: nullableString(value.touch_at),
    run_id: nullableString(value.run_id),
  };
}

function mapContactConversationMessage(
  record: Record<string, unknown>,
): ContactConversationMessage {
  return {
    ...record,
    id: stringValue(
      record.id ?? record.source_id ?? record.provider_message_id,
    ),
    source_id: stringValue(record.source_id ?? record.id),
    contact_id: stringValue(record.contact_id ?? record.whatsapp_user_id),
    whatsapp_user_id: stringValue(record.whatsapp_user_id ?? "unknown"),
    session_id: nullableString(record.session_id),
    workload_id: nullableString(record.workload_id),
    direction: stringValue(record.direction ?? "outbound"),
    role: stringValue(record.role ?? "assistant"),
    message_type: nullableString(record.message_type),
    message_kind: nullableString(record.message_kind),
    text: nullableString(record.text ?? record.body),
    body: nullableString(record.body),
    provider_message_id: nullableString(record.provider_message_id),
    provider_status: nullableString(record.provider_status),
    provider_error_code: nullableString(record.provider_error_code),
    provider_error_title: nullableString(record.provider_error_title),
    provider_error_details: nullableString(record.provider_error_details),
    occurred_at: stringValue(record.occurred_at ?? record.created_at),
    status_updated_at: nullableString(record.status_updated_at),
    failed_at: nullableString(record.failed_at),
    source: stringValue(record.source ?? "aws-live"),
    source_confidence: nullableString(record.source_confidence),
    raw_payload_s3:
      (record.raw_payload_s3 as ContactConversationMessage["raw_payload_s3"]) ??
      null,
    raw_payload: (record.raw_payload as Record<string, unknown>) ?? undefined,
    handling_outcome: nullableString(record.handling_outcome),
    bot_reply_kind: nullableString(record.bot_reply_kind),
    flow_step: nullableString(record.flow_step),
    sender_phone_number_id: nullableString(record.sender_phone_number_id),
  } satisfies ContactConversationMessage;
}

// --- Customer messages inbox -------------------------------------------------

const INBOX_ATTENTION_STATES = new Set([
  "needs_reply",
  "claimed",
  "resolved",
  "none",
]);

function emptyInboxRecord(contactId: Identifier): Record<string, unknown> {
  return {
    id: contactId,
    contact_id: contactId,
    whatsapp_user_id: contactId,
    attention_state: "none",
  };
}

function mapInboxConversation(
  record: Record<string, unknown>,
  fallbackContactId?: Identifier,
): InboxConversation {
  const contactId = stringValue(
    record.contact_id ??
      record.whatsapp_user_id ??
      record.id ??
      fallbackContactId,
  );
  const state = stringValue(record.attention_state);
  return {
    ...record,
    id: stringValue(record.id ?? contactId),
    contact_id: contactId,
    whatsapp_user_id: stringValue(record.whatsapp_user_id ?? contactId),
    attention_state: (INBOX_ATTENTION_STATES.has(state)
      ? state
      : "none") as InboxConversation["attention_state"],
    attention_priority: nullableString(record.attention_priority),
    attention_since: nullableString(record.attention_since),
    attention_count: optionalNumberValue(record.attention_count) ?? null,
    attention_reason: nullableString(record.attention_reason),
    attention_reason_label: nullableString(record.attention_reason_label),
    attention_reasons: Array.isArray(record.attention_reasons)
      ? record.attention_reasons.map(String)
      : null,
    attention_preview: nullableString(record.attention_preview),
    attention_claimed_by_name: nullableString(record.attention_claimed_by_name),
    attention_resolved_by_name: nullableString(
      record.attention_resolved_by_name,
    ),
    contact_name: nullableString(record.contact_name),
    first_name: nullableString(record.first_name),
    last_name: nullableString(record.last_name),
    phone_e164: nullableString(record.phone_e164),
    phone_country_iso2: nullableString(record.phone_country_iso2),
    freeform_window_expires_at: nullableString(
      record.freeform_window_expires_at,
    ),
  } satisfies InboxConversation;
}

function mapInboxSummary(record: Record<string, unknown>): InboxSummary {
  const byPriority =
    record.needs_reply_by_priority &&
    typeof record.needs_reply_by_priority === "object"
      ? (record.needs_reply_by_priority as Record<string, unknown>)
      : {};
  return {
    needs_reply: numberValue(record.needs_reply),
    claimed: numberValue(record.claimed),
    resolved: numberValue(record.resolved),
    needs_reply_by_priority: {
      high: numberValue(byPriority.high),
      normal: numberValue(byPriority.normal),
      low: numberValue(byPriority.low),
    },
    oldest_needs_reply_since: nullableString(record.oldest_needs_reply_since),
    generated_at: nullableString(record.generated_at),
  };
}

async function transitionInboxConversation(
  contactId: Identifier,
  action: "claim" | "release" | "resolve" | "reopen",
  input: InboxTransitionInput = {},
): Promise<InboxTransitionResult> {
  const body: InboxTransitionInput = {};
  if (input.operator_name) body.operator_name = input.operator_name;
  if (input.note?.trim()) body.note = input.note.trim();
  if (action === "release" && input.force === true) body.force = true;
  const response = await awsDashboardRequest<{
    data?: Record<string, unknown>;
    previous_state?: string | null;
  }>(`/whatsapp/inbox/${encodeURIComponent(String(contactId))}/${action}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  return {
    data: mapInboxConversation(
      response.data ?? emptyInboxRecord(contactId),
      contactId,
    ),
    previous_state: response.previous_state ?? null,
  };
}

function mapFeedback(
  record: Record<string, unknown>,
): EchoflicksSessionFeedback {
  return {
    ...record,
    id: stringValue(record.id ?? record.source_id),
    source_id: stringValue(record.source_id ?? record.id),
    session_id: nullableString(record.session_id),
    contact_id: nullableString(record.contact_id ?? record.whatsapp_user_id),
    whatsapp_user_id: stringValue(record.whatsapp_user_id ?? "unknown"),
    scene_id:
      typeof record.scene_id === "number" || typeof record.scene_id === "string"
        ? record.scene_id
        : null,
    video_timestamp_ms: optionalNumberValue(record.video_timestamp_ms) ?? null,
    video_frame: optionalNumberValue(record.video_frame) ?? null,
    feedback_category: nullableString(record.feedback_category),
    source: stringValue(record.source ?? "aws-live"),
  } satisfies EchoflicksSessionFeedback;
}

// Exported for tests (EchoCostSection.test.ts).
export function mapCostLineItem(
  record: Record<string, unknown>,
): EchoflicksSessionCostLineItem {
  const id = stringValue(record.usage_id ?? record.id ?? record.source_id);
  return {
    ...record,
    id,
    source_id: stringValue(record.source_id ?? id),
    usage_id: id,
    session_id: nullableString(record.session_id),
    contact_id: nullableString(record.contact_id ?? record.whatsapp_user_id),
    whatsapp_user_id: nullableString(record.whatsapp_user_id),
    occurred_at: nullableString(record.occurred_at),
    // Legacy rows predate the cross-domain ledger and are always i2v.
    cost_domain: nullableString(record.cost_domain) ?? "i2v",
    usage_detail: stringMap(record.usage_detail),
    segment_type: nullableString(record.segment_type),
    scene_id:
      typeof record.scene_id === "number" || typeof record.scene_id === "string"
        ? record.scene_id
        : nullableString(record.scene_id),
    scene_index:
      typeof record.scene_index === "number" ||
      typeof record.scene_index === "string"
        ? record.scene_index
        : nullableString(record.scene_index),
    scene_label: nullableString(record.scene_label),
    source_scene_type: nullableString(record.source_scene_type),
    provider_id: nullableString(record.provider_id),
    concrete_provider_id: nullableString(record.concrete_provider_id),
    provider_tier: nullableString(record.provider_tier),
    provider_account: nullableString(record.provider_account),
    model_id: nullableString(record.model_id),
    model_version: nullableString(record.model_version),
    provider_mode: nullableString(record.provider_mode),
    resolution: nullableString(record.resolution),
    aspect_ratio: nullableString(record.aspect_ratio),
    requested_duration_seconds:
      optionalNumberValue(record.requested_duration_seconds) ?? null,
    provider_duration_seconds:
      optionalNumberValue(record.provider_duration_seconds) ?? null,
    billable_quantity: optionalNumberValue(record.billable_quantity) ?? null,
    billable_unit: nullableString(record.billable_unit),
    unit_price_usd: optionalNumberValue(record.unit_price_usd) ?? null,
    estimated_cost_usd: optionalNumberValue(record.estimated_cost_usd) ?? null,
    provider_reported_cost_usd:
      optionalNumberValue(record.provider_reported_cost_usd) ?? null,
    provider_reported_credits:
      optionalNumberValue(record.provider_reported_credits) ?? null,
    effective_cost_usd: optionalNumberValue(record.effective_cost_usd) ?? null,
    currency: nullableString(record.currency),
    price_source: nullableString(record.price_source),
    price_version: nullableString(record.price_version),
    pricing_effective_date: nullableString(record.pricing_effective_date),
    task_id: nullableString(record.task_id),
    attempt_number: optionalNumberValue(record.attempt_number) ?? null,
    max_attempts: optionalNumberValue(record.max_attempts) ?? null,
    lifecycle_status: nullableString(record.lifecycle_status),
    qc_disposition: nullableString(record.qc_disposition),
    final_s3_key: nullableString(record.final_s3_key),
    billable: optionalBooleanValue(record.billable),
    confidence: nullableString(record.confidence),
    disposition: nullableString(record.disposition),
    // HDW contract v1 §4: indexer-owned, HD rows only; null on older rows.
    consumption_outcome: nullableString(record.consumption_outcome),
    source: nullableString(record.source),
    raw_metadata: (record.raw_metadata as Record<string, unknown>) ?? record,
  } satisfies EchoflicksSessionCostLineItem;
}

function mapEvent(record: Record<string, unknown>): EchoflicksSessionEvent {
  return {
    ...record,
    id: stringValue(record.id ?? record.source_id),
    source_id: stringValue(record.source_id ?? record.id),
    session_id: nullableString(record.session_id),
    contact_id: nullableString(record.contact_id ?? record.whatsapp_user_id),
    whatsapp_user_id: stringValue(record.whatsapp_user_id ?? "unknown"),
    event_type: stringValue(record.event_type ?? "unknown"),
    source: stringValue(record.source ?? "aws-live"),
  } satisfies EchoflicksSessionEvent;
}

function nullableString(value: unknown) {
  if (value == null || value === "") return null;
  return String(value);
}

function nullableTemplateReason(value: unknown) {
  const text = nullableString(value)?.trim();
  if (!text || text.toLowerCase() === "none") return null;
  return text;
}

function nullableNumber(value: unknown) {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function stringValue(value: unknown) {
  return value == null ? "" : String(value);
}

function numberValue(value: unknown) {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number : 0;
}

function nullableBoolean(value: unknown) {
  if (value == null) return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes"].includes(normalized)) return true;
    if (["false", "0", "no", ""].includes(normalized)) return false;
  }
  return Boolean(value);
}

function optionalNumberValue(value: unknown) {
  if (value == null || value === "") return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

function optionalBooleanValue(value: unknown) {
  if (value == null || value === "") return null;
  if (typeof value === "boolean") return value;
  return !["false", "0", "no", "off"].includes(String(value).toLowerCase());
}

function moneyMap(value: unknown): Record<string, number | null> {
  if (!isRecord(value)) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(value).map(([key, amount]) => [
      key,
      optionalNumberValue(amount) ?? null,
    ]),
  );
}

/** moneyMap, but undefined when the field is absent (not a map). */
function optionalMoneyMap(
  value: unknown,
): Record<string, number | null> | undefined {
  return isRecord(value) ? moneyMap(value) : undefined;
}

/** Flat string map (usage_detail): coerce scalar values, drop nested ones. */
function stringMap(value: unknown): Record<string, string> | null {
  if (!isRecord(value)) {
    return null;
  }
  const entries = Object.entries(value)
    .filter(
      ([, item]) =>
        typeof item === "string" ||
        typeof item === "number" ||
        typeof item === "boolean",
    )
    .map(([key, item]) => [key, String(item)]);
  return entries.length ? Object.fromEntries(entries) : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value != null && !Array.isArray(value);
}

function inferMimeType(path: string | null) {
  if (!path) return null;
  const lowerPath = path.toLowerCase();
  if (lowerPath.endsWith(".mp4")) return "video/mp4";
  if (lowerPath.endsWith(".mov")) return "video/quicktime";
  if (lowerPath.endsWith(".mp3")) return "audio/mpeg";
  if (lowerPath.endsWith(".wav")) return "audio/wav";
  if (lowerPath.endsWith(".png")) return "image/png";
  if (lowerPath.endsWith(".webp")) return "image/webp";
  if (lowerPath.endsWith(".gif")) return "image/gif";
  if (lowerPath.endsWith(".avif")) return "image/avif";
  if (lowerPath.endsWith(".bmp")) return "image/bmp";
  if (lowerPath.endsWith(".tif") || lowerPath.endsWith(".tiff")) {
    return "image/tiff";
  }
  // iPhone photos arrive as HEIC/HEIF. Classify them as images so the UI treats
  // them as displayable; the browser can't decode HEIC natively, so
  // useDisplayableImage converts them client-side before rendering.
  if (lowerPath.endsWith(".heic")) return "image/heic";
  if (lowerPath.endsWith(".heif")) return "image/heif";
  if (lowerPath.endsWith(".jpg") || lowerPath.endsWith(".jpeg")) {
    return "image/jpeg";
  }
  if (lowerPath.endsWith(".json")) return "application/json";
  return null;
}
